/* MODIF CODEX GPT-5.6 Sol | 2026-08-27 | prix et kilométrage du Duster actualisés | ordre direct CEO, mission BOV2 Pavillon */
/* ============================================================
   AUTOMOTIVE EBM — LE PAVILLON À L'AUBE (site v3 immersif)
   Un pavillon de chasse contemporain dans une clairière française
   à l'aube. Le SERVICE au centre (le bureau du chasseur), l'occasion
   du moment sur le pont de l'atelier vitré — l'occupant change,
   la pièce reste (slot piloté par /assets/data/occasion.json).
   Three.js vanilla, vendorisé (zéro CDN, zéro build).
   3 effets signature (et pas un de plus) :
     1. les deux phares au loin — une chasse rentre
     2. la Révélation Honnête — faisceaux d'inspection en séquence
     3. la Présence du chasseur — manteau, silhouette, jamais un visage
   MODIF FABLE 5 | 2026-07-04 | création v3 (run V3 EXCELLENCE, mission P1)
   MODIF FABLE 5 | 2026-07-04 | finition P2 : LUT « aube EBM » (pass final),
     compileAsync + warm render (zéro jank d'entrée), curseur custom, regard
     magnétique, textures demi-résolution mobile, moteur lointain synchronisé
     aux phares (opt-in), captions loader narratives, a11y (aria-current, loader)
   MODIF FABLE 5 | 2026-07-08 | v3.1 « LE SOUFFLE » (frappe B hors-norme) :
     la brume devient une MATIÈRE qui sent le geste — le rayon du curseur (ou du
     doigt en orbite tactile) traverse chaque voile, la brume s'écarte, s'étire
     dans le sens du passage et se referme en ~3 s. La force dépend de la VITESSE
     du geste. Champ analytique par nappe (raycast plan + shader), pas de sim
     FBO : zéro asset, zéro dépendance, zéro draw call en plus. Les 3 effets
     signature restent seuls des ÉVÉNEMENTS ; le souffle est une propriété de la
     matière (critère Awwwards n°8, la « matière Lusion »). REDUCED : coupé.
   MODIF FABLE 5 | 2026-07-08 | v3.2 « L'ORDRE DE CHASSE » (frappe B, geste principal) :
     le moment business central cesse d'être un formulaire — l'avis de recherche
     s'écrit sous les yeux (fiche papier), LA LECTURE DU CHASSEUR répond en direct
     et sait DIRE NON (calibrée sur les fourchettes de chassables.json — données
     maison déjà publiées au mur des mandats, zéro invention), le scellement au
     tampon, puis la fiche VOLE s'épingler sur la carte de France du bureau
     (pont DOM → monde, texture canvas maison). Fallback no3d : tampon + email,
     sans vol. REDUCED : pose directe. A11y : le form reste la source, la fiche
     est décorative (aria-hidden), la voix est un role="status" aria-live.
   ============================================================ */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const CONTACT = 'contact@automotive-ebm.fr'; // contact pro (.fr) — boîte OVH active 06/07
const ACCENT = 0xff5b04;
const fmt = new Intl.NumberFormat('fr-FR');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- palette de l'aube ---------- */
const DAWN = {
  zenith: new THREE.Color(0x1b2337),   // indigo haut de ciel
  mid: new THREE.Color(0x5c5470),      // mauve
  horizon: new THREE.Color(0xc97b4a),  // cuivre
  glow: new THREE.Color(0xff9440),     // l'orange Atacama devient une heure du jour
  sun: new THREE.Color(0xffe7bd),
  fog: new THREE.Color(0x51434a),      // brume mauve-grise
};

/* ---------- état global ---------- */
const S = {
  data: null, occ: null,
  renderer: null, scene: null, camera: null, composer: null,
  usePost: true, clock: new THREE.Clock(),
  pickables: [], markers: {}, zones: [], cones: [],
  paintMat: null, screenBlink: [],
  mist: [], dust: null, shafts: [],
  hunt: null,        // les phares au loin (effet signature n°1)
  passer: null,      // la silhouette derrière le verre (effet n°3)
  pinBoard: null,    // v3.2 : la carte du terrain de chasse (Europe) — cible de l'épinglage
  flight: null,      // v3.2 : le vol de l'avis de recherche vers la carte
  pinned: null,      // v3.2 : l'avis épinglé (un seul — le vôtre)
  lastOrder: null,   // v3.2 : le dernier ordre scellé (pour la texture)
  photoSky: null,    // SITE++ : la sphère ciel photo (aube réelle, fade-in différé)
  pinsPhoto: null,   // SITE++ : les pins billboards photo (4 variantes + near)
  pinsOld: null,     // les croisillons procéduraux, retirés après le fondu
  focused: null, hovered: null,
  rev: { timer: null, idx: -1, targets: [0, 0, 0, 0, 0], body: 0 },
  perf: { frames: 0, acc: 0, checked: false },
};

/* ---------- caméra : les pièces du pavillon ---------- */
const V3 = (x, y, z) => new THREE.Vector3(x, y, z);
const VIEWS = {
  orbit:     { pos: V3(0, 1.85, 6.8),    tgt: V3(0, 1.15, -2.0) },
  bureau:    { pos: V3(0, 1.5, 0.6),     tgt: V3(0, 1.12, -2.6) },
  occasion:  { pos: V3(5.95, 1.8, 2.45),  tgt: V3(8.45, 1.15, -3.0) },
  preuves:   { pos: V3(-4.35, 1.95, -2.05), tgt: V3(-5.75, 0.72, -3.75) },
  manifeste: { pos: V3(-2.6, 1.5, 1.7),  tgt: V3(-4.5, 1.3, 3.6) },
  prouveur:  { pos: V3(1.55, 1.6, 4.35), tgt: V3(3.25, 1.15, 2.65) },
  galerie:   { pos: V3(-7.2, 1.8, -2.0), tgt: V3(-10.8, 1.85, -2.0) },
};
const STATIONS = {
  bureau:    { label: 'Le bureau du chasseur — votre ordre de chasse', marker: [0, 2.15, -2.6], menu: 'Le bureau' },
  occasion:  { label: "L'atelier — l'occasion du moment",              marker: [8.15, 2.9, -2.7], menu: "L'occasion" },
  preuves:   { label: 'La table des preuves — le tableau de chasse',   marker: [-5.7, 1.75, -3.7], menu: 'Les preuves' },
  manifeste: { label: 'Le manteau du chasseur — le manifeste',         marker: [-4.5, 2.35, 3.6], menu: 'Le manteau' },
  prouveur:  { label: 'Le terminal — le Prouveur',                     marker: [3.15, 2.1, 2.75], menu: 'Le Prouveur' },
  galerie:   { label: 'Le mur des chasses — 15 chassables',            marker: [-10.5, 3.35, -2.0], menu: 'Les chasses' },
};

const rig = {
  mode: 'boot', // boot -> intro -> orbit <-> tween <-> focus
  baseAngle: 0, drag: 0, pitch: 0,
  mouse: { x: 0, y: 0 },
  cur: { pos: V3(3.2, 2.7, 20.5), tgt: V3(0, 1.6, -2) }, // départ : dehors, dans la clairière
  tween: null,
};
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
function tweenTo(pose, dur, done) {
  if (REDUCED) dur = 0.03; // reduced-motion : coupe franche, pas de travelling
  rig.tween = {
    t0: performance.now(), dur: dur * 1000,
    fp: rig.cur.pos.clone(), ft: rig.cur.tgt.clone(),
    tp: pose.pos.clone(), tt: pose.tgt.clone(), done,
  };
  rig.mode = 'tween';
}

/* ---------- DOM ---------- */
const $ = (s) => document.querySelector(s);
const dom = {
  loader: $('#loader'), bar: $('#loader-bar'), cap: $('#loader-caption'),
  canvas: $('#scene'), entry: $('#entry-title'), hint: $('#hint'),
  label: $('#hover-label'), panel: $('#panel'), panelC: $('#panel-content'),
  close: $('#panel-close'), sound: $('#btn-sound'), rooms: $('#rooms'),
};

/* ============================================================
   SON — l'aube synthétisée (vent bas + oiseaux lointains), opt-in
   ============================================================ */
const sound = {
  on: false, ctx: null, master: null, birdTimer: null,
  ensure() {
    if (this.ctx) return;
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return;
    this.ctx = new C();
    const master = this.ctx.createGain(); master.gain.value = 0; master.connect(this.ctx.destination);
    this.master = master;
    /* vent : bruit filtré très bas */
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
    const noise = this.ctx.createBufferSource(); noise.buffer = buf; noise.loop = true;
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 240; lp.Q.value = 0.4;
    const windGain = this.ctx.createGain(); windGain.gain.value = 0.5;
    noise.connect(lp); lp.connect(windGain); windGain.connect(master); noise.start();
    const lfo = this.ctx.createOscillator(); lfo.frequency.value = 0.07;
    const lg = this.ctx.createGain(); lg.gain.value = 90;
    lfo.connect(lg); lg.connect(lp.frequency); lfo.start();
    /* oiseaux : chœur de l'aube, chants brefs aléatoires */
    const chirp = () => {
      if (!this.on) return;
      const n = 2 + Math.floor(Math.random() * 3);
      const f0 = 2300 + Math.random() * 1700;
      for (let i = 0; i < n; i++) {
        const t = this.ctx.currentTime + i * (0.11 + Math.random() * 0.07);
        const o = this.ctx.createOscillator(), e = this.ctx.createGain();
        o.type = 'sine';
        o.frequency.setValueAtTime(f0 + Math.random() * 500, t);
        o.frequency.exponentialRampToValueAtTime(f0 * (0.62 + Math.random() * 0.2), t + 0.08);
        e.gain.setValueAtTime(0.0001, t);
        e.gain.exponentialRampToValueAtTime(0.006 + Math.random() * 0.005, t + 0.018);
        e.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
        o.connect(e); e.connect(master); o.start(t); o.stop(t + 0.12);
      }
    };
    const loop = () => {
      chirp();
      this.birdTimer = setTimeout(loop, 2600 + Math.random() * 6500);
    };
    loop();
    /* P2 — le moteur lointain de la chasse qui rentre (suit les deux phares) :
       deux sawtooth désaccordés très bas + passe-bas serré = un 4-cylindres à 800 m */
    const eng1 = this.ctx.createOscillator(); eng1.type = 'sawtooth'; eng1.frequency.value = 46;
    const eng2 = this.ctx.createOscillator(); eng2.type = 'sawtooth'; eng2.frequency.value = 46.7;
    const elp = this.ctx.createBiquadFilter(); elp.type = 'lowpass'; elp.frequency.value = 130; elp.Q.value = 0.7;
    this.engGain = this.ctx.createGain(); this.engGain.gain.value = 0;
    eng1.connect(elp); eng2.connect(elp); elp.connect(this.engGain); this.engGain.connect(master);
    eng1.start(); eng2.start();
    this.engOsc = [eng1, eng2];
  },
  engine(level, k) {
    /* level 0..1 = présence des phares ; k = progression de la course (doppler léger) */
    if (!this.ctx || !this.engGain) return;
    const t = this.ctx.currentTime;
    this.engGain.gain.setTargetAtTime(this.on ? level * 0.22 : 0, t, 0.25);
    if (this.engOsc && k !== undefined) {
      const f = 42 + (1 - k) * 9; // la voiture approche puis s'éloigne : le ton descend
      this.engOsc[0].frequency.setTargetAtTime(f, t, 0.4);
      this.engOsc[1].frequency.setTargetAtTime(f * 1.014, t, 0.4);
    }
  },
  toggle() {
    this.on = !this.on;
    if (this.on) { this.ensure(); this.ctx && this.ctx.resume(); }
    if (this.master) this.master.gain.linearRampToValueAtTime(this.on ? 0.055 : 0, this.ctx.currentTime + 1.2);
    dom.sound.querySelector('span').textContent = this.on ? 'on' : 'off';
    dom.sound.setAttribute('aria-pressed', String(this.on));
  },
  blip(f, d, g) {
    if (!this.on || !this.ctx) return;
    const o = this.ctx.createOscillator(), e = this.ctx.createGain();
    o.frequency.value = f; o.type = 'sine';
    e.gain.setValueAtTime(g, this.ctx.currentTime);
    e.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + d);
    o.connect(e); e.connect(this.ctx.destination); o.start(); o.stop(this.ctx.currentTime + d);
  },
  click() { this.blip(290, 0.12, 0.045); this.blip(580, 0.06, 0.018); },
  hover() { this.blip(700, 0.04, 0.01); },
};
dom.sound.addEventListener('click', () => sound.toggle());

/* ============================================================
   TEXTURES CANVAS (matières, écrans, cartes, plaques)
   ============================================================ */
/* P2 : budget textures mobile — demi-résolution < 900px (mêmes coordonnées de dessin) */
const TXS = window.innerWidth < 900 ? 0.5 : 1;
function cnv(w, h, draw) {
  const c = document.createElement('canvas');
  c.width = Math.round(w * TXS); c.height = Math.round(h * TXS);
  const ctx = c.getContext('2d');
  ctx.scale(TXS, TXS);
  draw(ctx, w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}
const F = (wght, px) => `${wght} ${px}px 'Archivo V', 'Segoe UI', Arial, sans-serif`;
const FM = (px) => `${px}px 'Cascadia Mono', 'Consolas', monospace`;

/* béton ciré — taches douces + moucheture + arcs de taloche */
function texBeton() {
  const t = cnv(512, 512, (x, w, h) => {
    x.fillStyle = '#26221e'; x.fillRect(0, 0, w, h);
    for (let i = 0; i < 26; i++) {
      const gx = Math.random() * w, gy = Math.random() * h, r = 40 + Math.random() * 120;
      const g = x.createRadialGradient(gx, gy, 0, gx, gy, r);
      const l = Math.random() > 0.5;
      g.addColorStop(0, l ? 'rgba(62,56,49,0.16)' : 'rgba(12,10,9,0.18)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g; x.fillRect(gx - r, gy - r, r * 2, r * 2);
    }
    for (let i = 0; i < 2400; i++) {
      x.fillStyle = `rgba(${Math.random() > 0.5 ? '255,240,224' : '0,0,0'},${Math.random() * 0.045})`;
      x.fillRect(Math.random() * w, Math.random() * h, 1.6, 1.6);
    }
    x.strokeStyle = 'rgba(255,244,230,0.022)'; x.lineWidth = 9;
    for (let i = 0; i < 7; i++) {
      x.beginPath();
      x.arc(Math.random() * w, Math.random() * h, 70 + Math.random() * 90, Math.random() * 6.3, Math.random() * 2 + 1);
      x.stroke();
    }
    x.strokeStyle = 'rgba(0,0,0,0.35)'; x.lineWidth = 2.5;
    [170, 342].forEach((p) => {
      x.beginPath(); x.moveTo(p, 0); x.lineTo(p, h); x.stroke();
      x.beginPath(); x.moveTo(0, p); x.lineTo(w, p); x.stroke();
    });
  });
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 3);
  return t;
}

/* bois brûlé shou-sugi-ban — lattes verticales charbon, veinage craquelé */
function texBoisBrule() {
  const t = cnv(512, 512, (x, w, h) => {
    x.fillStyle = '#0d0b09'; x.fillRect(0, 0, w, h);
    const lat = 64; // largeur d'une latte
    for (let lx = 0; lx < w; lx += lat) {
      const shade = 8 + Math.random() * 10;
      x.fillStyle = `rgb(${shade + 4},${shade + 1},${shade - 2})`;
      x.fillRect(lx, 0, lat - 2, h);
      x.fillStyle = 'rgba(0,0,0,0.85)'; x.fillRect(lx + lat - 3, 0, 3, h); // creux entre lattes
      /* veinage vertical */
      for (let v = 0; v < 9; v++) {
        const vx = lx + 4 + Math.random() * (lat - 10);
        x.strokeStyle = `rgba(${Math.random() > 0.6 ? '58,48,38' : '0,0,0'},${0.12 + Math.random() * 0.2})`;
        x.lineWidth = 0.8 + Math.random() * 1.4;
        x.beginPath(); x.moveTo(vx, 0);
        for (let vy = 0; vy <= h; vy += 32) x.lineTo(vx + Math.sin(vy * 0.04 + vx) * 2.4, vy);
        x.stroke();
      }
      /* craquelures du brûlage */
      for (let cq = 0; cq < 5; cq++) {
        const cy = Math.random() * h;
        x.strokeStyle = 'rgba(0,0,0,0.5)'; x.lineWidth = 1;
        x.beginPath(); x.moveTo(lx + 2, cy); x.lineTo(lx + lat - 4, cy + (Math.random() - 0.5) * 8); x.stroke();
      }
      /* reflet charbon (le brûlé accroche la lumière) */
      const g = x.createLinearGradient(lx, 0, lx + lat, 0);
      g.addColorStop(0, 'rgba(120,110,105,0)');
      g.addColorStop(0.5, `rgba(120,112,108,${0.03 + Math.random() * 0.05})`);
      g.addColorStop(1, 'rgba(120,110,105,0)');
      x.fillStyle = g; x.fillRect(lx, 0, lat, h);
    }
  });
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(2.5, 1);
  return t;
}

/* écran du bureau : l'ordre de chasse */
function texScreenConfig() {
  return cnv(512, 320, (x, w, h) => {
    x.fillStyle = '#0c0906'; x.fillRect(0, 0, w, h);
    x.strokeStyle = 'rgba(255,91,4,0.35)'; x.strokeRect(10, 10, w - 20, h - 20);
    x.fillStyle = '#ff5b04'; x.font = F(800, 26); x.fillText('ORDRE DE CHASSE', 32, 56);
    x.fillStyle = '#efe9e1'; x.font = FM(15);
    ['> modele ........ a definir', '> budget ........ 8 000 - 30 000 EUR', '> releve marche . publie avant ordre', '> statut ........ EN ATTENTE DU CLIENT_'].forEach((l, i) => x.fillText(l, 32, 100 + i * 30));
    x.strokeStyle = 'rgba(255,91,4,0.8)'; x.lineWidth = 2;
    x.beginPath(); x.arc(430, 230, 34, 0, Math.PI * 2); x.stroke();
    x.beginPath(); x.moveTo(430, 182); x.lineTo(430, 278); x.moveTo(382, 230); x.lineTo(478, 230); x.stroke();
  });
}
/* écran du terminal : le Prouveur */
function texScreenProuveur() {
  return cnv(512, 320, (x, w, h) => {
    x.fillStyle = '#080a08'; x.fillRect(0, 0, w, h);
    x.fillStyle = '#ff5b04'; x.font = FM(17); x.fillText('PROUVEUR v1 — pret.', 26, 48);
    x.fillStyle = '#c9c2b8'; x.font = FM(14);
    ['> deposez une capture PNG/JPEG', '> analyse : environ 60 secondes', '> points de vigilance + questions', '> lecture du prix, argumentee', '', '> gratuit. meme sans acheter ici._'].forEach((l, i) => x.fillText(l, 26, 84 + i * 28));
  });
}
/* MODIF CODEX GPT-5.6 Sol | 2026-08-28 16:40 +02:00 | écran 3D du Prouveur aligné sur son entrée et son délai réels | ordre direct CEO de finir les missions Business Opus | rollback : pavillon.js.bak_20260828_164024_preuves_prouveur */

/* la carte du terrain de chasse — l’Europe de l’Ouest épinglée au mur du bureau.
   Doctrine P11 : la CHASSE = l’Europe, la LIVRAISON = la France. La France est
   distinguée (lavis d’aube + trait appuyé) ; les épingles Atacama marquent les
   terrains hors-France et les fils convergent vers le point de livraison français.
   MODIF FABLE 5 (W6) | 2026-07-10 | France-seule -> Europe de l’Ouest, livrée en France */
function texCarteChasse() {
  return cnv(768, 576, (x, w, h) => {
    /* liège / lin sombre */
    x.fillStyle = '#191411'; x.fillRect(0, 0, w, h);
    for (let i = 0; i < 900; i++) {
      x.fillStyle = `rgba(${Math.random() > 0.5 ? '210,180,150' : '0,0,0'},${Math.random() * 0.05})`;
      x.fillRect(Math.random() * w, Math.random() * h, 2, 2);
    }
    x.fillStyle = '#efe9e1'; x.font = F(800, 30); x.fillText('LE TERRAIN DE CHASSE', 40, 58);
    x.fillStyle = '#8f867b'; x.font = F(500, 17);
    x.fillText('La chasse s’ouvre à l’Europe de l’Ouest — la livraison se fait en France', 40, 88);

    /* la feuille carte, punaisée */
    x.save(); x.translate(76, 118); x.rotate(-0.012);
    x.fillStyle = '#211c17'; x.fillRect(0, 0, 442, 418);
    x.strokeStyle = 'rgba(239,233,225,0.14)'; x.strokeRect(0, 0, 442, 418);

    /* mini-projection lon/lat -> pixels (équirectangulaire, longitudes corrigées
       par le cosinus de la latitude moyenne). Silhouette lisible, pas un atlas. */
    const GEO = { lon0: -10, lat1: 55, k: 0.705, s: 19, ox: 30, oy: 26 };
    const gp = (lon, lat) => [GEO.ox + (lon - GEO.lon0) * GEO.k * GEO.s, GEO.oy + (GEO.lat1 - lat) * GEO.s];
    const trace = (pts) => {
      x.beginPath();
      pts.forEach((c, i) => { const p = gp(c[0], c[1]); i ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1]); });
      x.closePath();
    };

    /* les pays voisins — contours ivoire fins (le terrain de chasse) */
    const IBERIE = [[-1.8, 43.3], [2.9, 42.4], [2.1, 41.4], [0.0, 39.5], [-0.9, 37.8], [-2.5, 36.8], [-5.6, 36.0], [-7.0, 37.2], [-9.1, 38.7], [-8.6, 41.1], [-9.0, 42.9], [-3.7, 43.4]];
    const ITALIE = [[7.0, 45.1], [9.2, 46.0], [13.0, 45.7], [13.5, 43.6], [16.8, 41.1], [18.2, 40.1], [17.0, 40.0], [15.6, 38.1], [14.3, 40.8], [12.5, 41.9], [10.3, 43.5], [8.9, 44.4]];
    const SICILE = [[12.4, 38.1], [15.1, 38.2], [15.1, 36.7], [12.4, 37.6]];
    const ALLEMAGNE = [[7.6, 47.9], [6.4, 49.5], [6.1, 50.8], [7.0, 53.3], [9.5, 54.5], [12.1, 54.2], [14.6, 52.5], [14.4, 50.9], [13.4, 48.6], [11.4, 47.6], [9.2, 47.7]];
    const BELGIQUE = [[2.5, 51.1], [4.2, 51.4], [5.9, 50.8], [5.8, 49.5], [4.0, 49.6], [2.6, 50.5]];
    const PAYSBAS = [[3.4, 51.4], [4.0, 53.3], [6.8, 53.4], [6.1, 51.9], [5.9, 50.8]];
    const SUISSE = [[6.1, 47.4], [8.6, 47.8], [9.6, 46.9], [8.0, 45.9], [6.9, 46.0]];
    const AUTRICHE = [[9.6, 47.4], [13.0, 48.3], [16.9, 48.6], [16.0, 46.7], [12.8, 46.6], [10.0, 46.8]];
    const FRANCE = [[3.0, 51.0], [7.5, 49.5], [7.6, 48.3], [6.9, 46.5], [7.3, 43.7], [5.4, 43.3], [3.9, 43.4], [3.0, 42.5], [-1.5, 43.3], [-1.2, 44.7], [-1.2, 46.2], [-2.9, 47.3], [-4.7, 48.3], [-2.0, 48.6], [-1.6, 49.7], [0.1, 49.5], [1.9, 50.9]];

    x.lineJoin = 'round';
    x.strokeStyle = 'rgba(239,233,225,0.42)'; x.lineWidth = 1.2;
    [IBERIE, ITALIE, SICILE, ALLEMAGNE, BELGIQUE, PAYSBAS, SUISSE, AUTRICHE].forEach((c) => { trace(c); x.stroke(); });

    /* Portugal — un filet intérieur discret */
    x.strokeStyle = 'rgba(239,233,225,0.24)'; x.lineWidth = 1;
    x.beginPath();
    [[-8.2, 42.0], [-7.2, 40.0], [-7.4, 37.2]].forEach((c, i) => { const p = gp(c[0], c[1]); i ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1]); });
    x.stroke();

    /* LA FRANCE — distinguée : lavis d’aube (~15 %) + trait appuyé */
    trace(FRANCE);
    x.fillStyle = 'rgba(255,148,64,0.15)'; x.fill();
    x.strokeStyle = 'rgba(239,233,225,0.72)'; x.lineWidth = 2.6; x.stroke();

    /* les terrains de chasse — épingles Atacama HORS France ; les fils convergent
       vers la livraison (Belgique, Rhénanie, Bavière, Milanais, Madrid) */
    const HUB = gp(2.6, 46.8);
    const CHASSE = [[4.6, 50.6], [8.6, 50.0], [11.4, 48.2], [9.1, 45.4], [-3.7, 40.4]];
    x.strokeStyle = 'rgba(255,91,4,0.30)'; x.lineWidth = 1.1;
    CHASSE.forEach((c) => { const p = gp(c[0], c[1]); x.beginPath(); x.moveTo(HUB[0], HUB[1]); x.lineTo(p[0], p[1]); x.stroke(); });
    CHASSE.forEach((c) => {
      const p = gp(c[0], c[1]);
      x.fillStyle = 'rgba(255,91,4,0.22)'; x.beginPath(); x.arc(p[0], p[1], 9, 0, Math.PI * 2); x.fill();
      x.fillStyle = '#ff5b04'; x.beginPath(); x.arc(p[0], p[1], 4.5, 0, Math.PI * 2); x.fill();
    });

    /* la livraison — France : un point calme, ivoire cerclé laiton (pas l’orange de l’acte) */
    x.fillStyle = '#efe9e1'; x.beginPath(); x.arc(HUB[0], HUB[1], 6, 0, Math.PI * 2); x.fill();
    x.strokeStyle = '#b08d3e'; x.lineWidth = 2; x.beginPath(); x.arc(HUB[0], HUB[1], 11, 0, Math.PI * 2); x.stroke();

    /* étiquettes manuscrites (mono, discrètes) */
    const tc = gp(3.5, 54.2);
    x.save(); x.translate(tc[0], tc[1]); x.rotate(-0.04);
    x.fillStyle = 'rgba(161,151,138,0.92)'; x.font = FM(14); x.fillText('terrain de chasse', 0, 0); x.restore();
    const lv = gp(-1.1, 44.5);
    x.save(); x.translate(lv[0], lv[1]); x.rotate(0.02);
    x.fillStyle = 'rgba(239,233,225,0.82)'; x.font = FM(13); x.fillText('livraison : France', 0, 0); x.restore();

    x.restore();

    /* deux relevés punaisés à droite (le relevé précède l’ordre) */
    const sheet = (sx, sy, rot, lines, barre) => {
      x.save(); x.translate(sx, sy); x.rotate(rot);
      x.fillStyle = '#221d18'; x.fillRect(0, 0, 168, 128);
      x.strokeStyle = 'rgba(239,233,225,0.13)'; x.strokeRect(0, 0, 168, 128);
      x.fillStyle = '#ff5b04'; x.font = F(700, 13); x.fillText(lines[0], 12, 24);
      x.fillStyle = '#a1978a'; x.font = FM(11);
      lines.slice(1).forEach((l, i) => x.fillText(l, 12, 46 + i * 20));
      if (barre) { x.strokeStyle = '#ff5b04'; x.lineWidth = 2; x.beginPath(); x.moveTo(10, 62); x.lineTo(150, 60); x.stroke(); }
      x.fillStyle = '#c9c2b8'; x.beginPath(); x.arc(84, 6, 4, 0, Math.PI * 2); x.fill();
      x.restore();
    };
    sheet(576, 130, 0.03, ['RELEVE MARCHE', 'releve du 30/08/2026', 'cote haute : 15 900', 'prix : 12 700 + 290'], true);
    sheet(586, 300, -0.025, ['ORDRE TYPE', 'cible figee ensemble', 'releve publie avant', 'tout engagement_'], false);
  });
}

/* la table des preuves — dossiers ouverts, cote barrée, tampon */
function texDossier(n) {
  return cnv(320, 224, (x, w, h) => {
    x.fillStyle = '#453a2c'; x.fillRect(0, 0, w, h); // chemise kraft sous la lampe
    x.strokeStyle = 'rgba(0,0,0,0.5)'; x.lineWidth = 2; x.beginPath(); x.moveTo(w / 2, 4); x.lineTo(w / 2, h - 4); x.stroke();
    /* page gauche : le relevé */
    x.fillStyle = '#6b6154'; x.fillRect(14, 12, w / 2 - 26, h - 24);
    x.fillStyle = '#1d1812'; x.font = FM(10);
    if (n === 0) {
      x.fillText('DUSTER TCe 125 — 2018', 24, 34);
      x.fillText('releve du 30/08/2026', 24, 52);
      x.fillStyle = '#33291f';
      ['14 990  74 000 km', '15 400  81 500 km', '15 900  66 900 km', '14 500  98 200 km'].forEach((l, i) => x.fillText(l, 24, 74 + i * 17));
      x.strokeStyle = '#ff5b04'; x.lineWidth = 1.6;
      x.beginPath(); x.moveTo(22, 70); x.lineTo(138, 68); x.stroke();
    } else {
      x.fillText(n === 1 ? 'VOTRE CHASSE' : 'LA SUIVANTE', 24, 34);
      x.fillStyle = '#4a4034';
      ['cote relevee ......', 'prix chasse .......', 'ecart, date .......'].forEach((l, i) => x.fillText(l, 24, 60 + i * 20));
    }
    /* page droite : la conclusion + tampon */
    x.fillStyle = '#6b6154'; x.fillRect(w / 2 + 12, 12, w / 2 - 26, h - 24);
    if (n === 0) {
      x.fillStyle = '#17130e'; x.font = F(800, 20); x.fillText('12 700', w / 2 + 26, 52);
      x.fillStyle = '#2c2118'; x.font = FM(10); x.fillText('EUR + 290 frais', w / 2 + 26, 72);
      x.save(); x.translate(w / 2 + 78, 140); x.rotate(-0.22);
      x.strokeStyle = 'rgba(224,74,0,0.95)'; x.lineWidth = 3; x.strokeRect(-58, -20, 116, 40);
      x.fillStyle = 'rgba(224,74,0,0.95)'; x.font = F(800, 17); x.fillText('VERIFIE', -34, 6);
      x.restore();
    } else {
      x.fillStyle = '#4a4034'; x.font = FM(10);
      x.fillText('publie ici le jour', w / 2 + 24, 60);
      x.fillText('de l’ordre — jamais', w / 2 + 24, 80);
      x.fillText('avant la preuve_', w / 2 + 24, 100);
    }
  });
}

/* LE MUR DES CHASSES — 15 avis de recherche en UN SEUL canvas (1 draw call) */
function texMurMandats(chassables) {
  return cnv(2048, 1024, (x, w, h) => {
    x.fillStyle = '#0e0b09'; x.fillRect(0, 0, w, h);
    x.fillStyle = '#efe9e1'; x.font = F(800, 52); x.fillText('LE MUR DES CHASSES', 64, 92);
    x.fillStyle = '#8f867b'; x.font = F(500, 26); x.fillText('15 voitures qu’on sait chasser — fourchettes relevées marché, affinées le jour de l’ordre de chasse', 64, 134);
    x.strokeStyle = 'rgba(255,91,4,0.8)'; x.lineWidth = 3; x.beginPath(); x.moveTo(64, 158); x.lineTo(360, 158); x.stroke();
    const CW = 380, CH = 252, GX = 20, GY = 22, OX = 64, OY = 200;
    chassables.slice(0, 15).forEach((c, i) => {
      const col = i % 5, row = Math.floor(i / 5);
      const cx = OX + col * (CW + GX), cy = OY + row * (CH + GY);
      /* fiche « avis de recherche » */
      x.fillStyle = '#151007'; x.fillRect(cx, cy, CW, CH);
      x.strokeStyle = 'rgba(239,233,225,0.16)'; x.lineWidth = 1.4; x.strokeRect(cx, cy, CW, CH);
      /* plaque laiton */
      const lg = x.createLinearGradient(cx, cy, cx + CW, cy);
      lg.addColorStop(0, '#6d5426'); lg.addColorStop(0.5, '#a8874a'); lg.addColorStop(1, '#5c4620');
      x.fillStyle = lg; x.fillRect(cx + 14, cy + 16, 128, 26);
      x.fillStyle = '#1c1409'; x.font = F(700, 15); x.fillText('AVIS DE RECHERCHE', 16 + cx, cy + 34);
      x.fillStyle = '#efe9e1'; x.font = F(750, 27);
      x.fillText(c.modele.length > 24 ? c.modele.slice(0, 24) : c.modele, cx + 16, cy + 84);
      x.fillStyle = '#8f867b'; x.font = F(500, 19);
      x.fillText(`${c.annees} · ${c.motorisation}`.slice(0, 34), cx + 16, cy + 116);
      x.fillStyle = '#ff5b04'; x.font = F(800, 30);
      x.fillText(`${fmt.format(c.fourchette[0])} – ${fmt.format(c.fourchette[1])} €`, cx + 16, cy + 164);
      x.fillStyle = '#6d655b'; x.font = F(500, 18); x.fillText(`chasse type : ${c.delai}`, cx + 16, cy + 196);
      x.fillStyle = '#5d564d'; x.font = FM(14); x.fillText(`ordre n° —  ·  ouvert`, cx + 16, cy + 228);
    });
  });
}

/* plaque de l'atelier — l'occasion du moment (pilotée par occasion.json) */
function texPlaqueAtelier(occ) {
  return cnv(1024, 256, (x, w, h) => {
    x.fillStyle = '#0b0a09'; x.fillRect(0, 0, w, h);
    x.strokeStyle = 'rgba(255,91,4,0.4)'; x.lineWidth = 3; x.strokeRect(8, 8, w - 16, h - 16);
    x.fillStyle = '#8f867b'; x.font = F(600, 30); x.fillText('L’OCCASION DU MOMENT', 42, 66);
    x.fillStyle = '#efe9e1'; x.font = F(800, 66); x.fillText(occ.plaque3d, 42, 150);
    x.fillStyle = '#ff5b04'; x.font = F(800, 52); x.fillText(occ.prixLabel, 42, 222);
    x.fillStyle = '#6d655b'; x.font = FM(20);
    x.fillText('la piece est permanente — l’occupant change', 460, 220);
  });
}

/* silhouette de pin (croisillon instancié) */
function texPin() {
  const c = document.createElement('canvas'); c.width = 128; c.height = 256;
  const x = c.getContext('2d');
  x.clearRect(0, 0, 128, 256);
  x.fillStyle = '#07060a';
  x.fillRect(58, 196, 12, 60); // tronc
  let wdt = 104;
  for (let i = 0; i < 7; i++) { // étages de branches, bords déchiquetés
    const y = 196 - i * 27;
    x.beginPath();
    x.moveTo(64 - wdt / 2, y);
    for (let k = 0; k <= 8; k++) {
      const px = 64 - wdt / 2 + (wdt / 8) * k;
      x.lineTo(px, y - 6 - Math.random() * 9);
    }
    x.lineTo(64, y - 34);
    x.closePath(); x.fill();
    wdt *= 0.78;
  }
  x.beginPath(); x.moveTo(56, 34); x.lineTo(64, 4); x.lineTo(72, 34); x.closePath(); x.fill();
  const t = new THREE.CanvasTexture(c);
  return t;
}

/* nappes de brume */
function texBrume() {
  const c = document.createElement('canvas'); c.width = 512; c.height = 128;
  const x = c.getContext('2d');
  x.clearRect(0, 0, 512, 128);
  for (let i = 0; i < 34; i++) {
    const gx = Math.random() * 512, gy = 40 + Math.random() * 60, r = 40 + Math.random() * 90;
    const g = x.createRadialGradient(gx, gy, 0, gx, gy, r);
    g.addColorStop(0, `rgba(238,220,208,${0.05 + Math.random() * 0.07})`);
    g.addColorStop(1, 'rgba(238,220,208,0)');
    x.fillStyle = g;
    x.save(); x.translate(gx, gy); x.scale(2.4, 1); x.translate(-gx, -gy);
    x.beginPath(); x.arc(gx, gy, r, 0, Math.PI * 2); x.fill(); x.restore();
  }
  return new THREE.CanvasTexture(c);
}

/* halo de phare */
function texGlow(inner, outer) {
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d');
  const g = x.createRadialGradient(64, 64, 2, 64, 64, 62);
  g.addColorStop(0, inner);
  g.addColorStop(0.25, outer);
  g.addColorStop(1, 'rgba(255,150,60,0)');
  x.fillStyle = g; x.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

/* silhouette humaine (la Présence — jamais un visage) */
function texSilhouette() {
  const c = document.createElement('canvas'); c.width = 128; c.height = 256;
  const x = c.getContext('2d');
  x.clearRect(0, 0, 128, 256);
  x.fillStyle = '#0b0a10';
  x.beginPath(); x.arc(64, 34, 15, 0, Math.PI * 2); x.fill(); // tête (de dos / à contre-jour)
  x.beginPath(); // épaules + manteau qui tombe
  x.moveTo(30, 62);
  x.quadraticCurveTo(64, 46, 98, 62);
  x.lineTo(104, 130);
  x.quadraticCurveTo(102, 196, 92, 232);
  x.lineTo(76, 232); x.lineTo(72, 160); x.lineTo(58, 160); x.lineTo(52, 232);
  x.lineTo(38, 232);
  x.quadraticCurveTo(26, 190, 26, 130);
  x.closePath(); x.fill();
  const t = new THREE.CanvasTexture(c);
  return t;
}

function texMarker() {
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d');
  x.strokeStyle = '#ff5b04'; x.lineWidth = 5;
  x.beginPath(); x.arc(64, 64, 44, 0, Math.PI * 2); x.stroke();
  x.fillStyle = 'rgba(255,91,4,0.14)';
  x.beginPath(); x.arc(64, 64, 44, 0, Math.PI * 2); x.fill();
  x.strokeStyle = '#ffe9da'; x.lineWidth = 6;
  x.beginPath(); x.moveTo(64, 40); x.lineTo(64, 88); x.moveTo(40, 64); x.lineTo(88, 64); x.stroke();
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/* ============================================================
   LE CIEL DE L'AUBE (shader procédural, zéro asset)
   ============================================================ */
const SUN_DIR = new THREE.Vector3(0.22, 0.055, -1).normalize();
function makeSky() {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false,
    uniforms: {
      uSun: { value: SUN_DIR.clone() },
      uZenith: { value: DAWN.zenith },
      uMid: { value: DAWN.mid },
      uHorizon: { value: DAWN.horizon },
      uGlow: { value: DAWN.glow },
      uSunCol: { value: DAWN.sun },
    },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      varying vec3 vDir;
      uniform vec3 uSun, uZenith, uMid, uHorizon, uGlow, uSunCol;
      float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453); }
      void main() {
        vec3 d = normalize(vDir);
        float y = clamp(d.y, -0.08, 1.0);
        /* gradient vertical en 2 temps : horizon -> mauve -> indigo */
        vec3 col = mix(uHorizon, uMid, smoothstep(0.0, 0.22, y));
        col = mix(col, uZenith, smoothstep(0.14, 0.72, y));
        /* la lueur chaude, concentrée autour de l'azimut du soleil */
        float sunAmt = max(dot(d, normalize(uSun)), 0.0);
        float band = exp(-pow(max(y, 0.0) * 5.2, 1.35));
        col += uGlow * band * pow(sunAmt, 2.6) * 1.15;
        /* le disque + le halo du soleil levant */
        col += uSunCol * pow(sunAmt, 900.0) * 5.0;
        col += uSunCol * pow(sunAmt, 90.0) * 0.4;
        /* stratus fins étirés près de l'horizon (ombres violettes) */
        float az = atan(d.x, -d.z);
        float cl = sin(y * 210.0 + sin(az * 4.0) * 2.2) * sin(y * 118.0 - az * 2.0);
        float clm = smoothstep(0.35, 0.9, cl) * exp(-pow(max(y - 0.055, 0.0) * 16.0, 1.6)) * step(0.015, y);
        col = mix(col, uZenith * 0.9 + uGlow * 0.16, clm * 0.34);
        /* dithering anti-banding */
        col += (hash(gl_FragCoord.xy) - 0.5) * 0.012;
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(260, 48, 32), mat);
}

/* SITE++ — LE CIEL PHOTO : l'aube RÉELLE (industrial_sunset, Poly Haven CC0,
   étalonnée « aube EBM » par skybox_prep.py — 38 Ko WebP).
   Chargée en DIFFÉRÉ après l'entrée : le shader procédural tient la première frame
   (0 octet, 0 jank), la photo fond dessus pendant le travelling — l'aube s'allume.
   toneMapped:false : le WebP est déjà étalonné offline ; la LUT écran unifie.
   L'environnement PBR reste le PMREM du shader (HDR par construction, cohérent). */
const SKY_PHOTO = {
  src2560: '/assets/img/sky/aube_2560.webp',
  src1536: '/assets/img/sky/aube_1536.webp',
  rot: -0.71, // soleil photo (u=0.6028, natif ~53° est) ramené sur l'azimut de SUN_DIR (~12° est de la trouée)
};
/* QA (captures reproductibles) : ?qa saute compile+travelling, ciel immédiat ; #vue=<station> cadre une pièce */
const QA = new URLSearchParams(window.location.search).has('qa');
function loadPhotoSky() {
  if (S.photoSky || (navigator.connection && navigator.connection.saveData)) return;
  const src = window.innerWidth < 900 ? SKY_PHOTO.src1536 : SKY_PHOTO.src2560;
  new THREE.TextureLoader().load(src, (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(256, 64, 40),
      new THREE.MeshBasicMaterial({
        map: tex, side: THREE.BackSide, fog: false, toneMapped: false,
        transparent: true, opacity: 0, depthWrite: false,
      })
    );
    m.rotation.y = SKY_PHOTO.rot;
    m.renderOrder = -1;
    S.renderer.initTexture(tex); // upload GPU hors frame visible
    if (QA) { m.material.opacity = 1; m.material.transparent = false; }
    S.scene.add(m);
    S.photoSky = { mesh: m, t0: performance.now() };
  });
}

/* ============================================================
   LE MONDE — clairière, forêt, brume, la chasse qui rentre
   ============================================================ */
function buildWorld(scene) {
  scene.add(makeSky());

  /* le sol de la clairière */
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(250, 48),
    new THREE.MeshStandardMaterial({ color: 0x15120e, roughness: 1, metalness: 0 })
  );
  ground.rotation.x = -Math.PI / 2; ground.position.y = -0.02;
  ground.receiveShadow = true;
  scene.add(ground);

  /* la crête des collines, au loin */
  const seg = 96, rr = 165;
  const pos = [], idx = [];
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    const hgt = 5 + Math.max(1.5, Math.sin(i * 0.37) * 3.2 + Math.sin(i * 0.13 + 2.1) * 4.2 + Math.sin(i * 0.71) * 1.5);
    pos.push(Math.sin(a) * rr, -2, Math.cos(a) * rr);
    pos.push(Math.sin(a) * rr, hgt, Math.cos(a) * rr);
  }
  for (let i = 0; i < seg; i++) {
    const b = i * 2;
    idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
  }
  const ridgeGeo = new THREE.BufferGeometry();
  ridgeGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  ridgeGeo.setIndex(idx);
  const ridge = new THREE.Mesh(ridgeGeo, new THREE.MeshBasicMaterial({ color: 0x18131d, side: THREE.DoubleSide }));
  scene.add(ridge);

  /* les pins — silhouettes instanciées (1 draw call) */
  const pinTex = texPin();
  const quad = new THREE.PlaneGeometry(1, 2.3);
  const quad2 = quad.clone(); quad2.rotateY(Math.PI / 2);
  const pinGeo = mergeGeoms([quad, quad2]);
  const pinMat = new THREE.MeshBasicMaterial({ map: pinTex, alphaTest: 0.42, side: THREE.DoubleSide, color: 0xffffff });
  const COUNT = 110;
  const pins = new THREE.InstancedMesh(pinGeo, pinMat, COUNT);
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), tv = new THREE.Vector3();
  let placed = 0, guard = 0;
  while (placed < COUNT && guard++ < 2000) {
    const a = Math.random() * Math.PI * 2;
    const r = 26 + Math.pow(Math.random(), 0.7) * 95;
    const px = Math.sin(a) * r, pz = Math.cos(a) * r;
    /* on dégage l'axe du regard (la trouée vers l'aube) et le corridor des phares */
    if (pz < -18 && pz > -75 && Math.abs(px) < 9) continue;         // trouée
    if (pz > 8 && pz < 30 && Math.abs(px) < 14) continue;           // devant l'entrée
    const s = 2.6 + Math.random() * 4.6;
    tv.set(px, s * 1.12, pz);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.random() * Math.PI);
    sc.set(s * (0.7 + Math.random() * 0.5), s, s * (0.7 + Math.random() * 0.5));
    m4.compose(tv, q, sc);
    pins.setMatrixAt(placed++, m4);
  }
  pins.instanceMatrix.needsUpdate = true;
  scene.add(pins);
  S.pinsOld = pins; // SITE++ : remplacés par les billboards photo après chargement

  /* la brume basse — nappes qui dérivent ET sentent le geste (v3.1 le Souffle).
     Le rayon du regard traverse chaque voile ; l'empreinte est calculée dans SES
     UV (exacte par nappe, d'où la profondeur : on touche les voiles l'un après
     l'autre le long du rayon). Le shader écarte la matière autour du passage
     (poussée radiale), l'entraîne dans le sens du geste (uFlow) et l'éclaircit —
     puis tout se referme quand l'énergie retombe. Réglages : rayon d'empreinte
     exp(-d²·7), poussée 0.55, entraînement uFlow, éclaircie 0.6. */
  const bTex = texBrume();
  const mistFrag = `
    uniform sampler2D map;
    uniform vec3 uColor;
    uniform float uOpacity, uEnergy, uAspect;
    uniform vec2 uTouch, uFlow;
    varying vec2 vUv;
    void main() {
      vec2 p = (vUv - uTouch) * vec2(uAspect, 1.0);
      float d2 = dot(p, p);
      float g = exp(-d2 * 7.0) * uEnergy;
      vec2 dir = p * inversesqrt(max(d2, 1e-6));
      vec2 disp = (dir * 0.55 + uFlow) * g * vec2(1.0 / uAspect, 1.0);
      vec4 tx = texture2D(map, vUv - disp);
      float a = tx.a * uOpacity * (1.0 - 0.6 * g);
      gl_FragColor = vec4(uColor * tx.rgb, a);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`;
  const mkMist = (mx, my, mz, mw, mh, op, spd) => {
    const mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, fog: false,
      uniforms: {
        map: { value: bTex },
        uColor: { value: new THREE.Color(0xcfae98) },
        uOpacity: { value: op },
        uEnergy: { value: 0 },
        uAspect: { value: mw / mh },
        uTouch: { value: new THREE.Vector2(-9, -9) },
        uFlow: { value: new THREE.Vector2() },
      },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: mistFrag,
    });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(mw, mh), mat);
    m.position.set(mx, my, mz);
    m.userData = { spd, x0: mx, amp: 6 + Math.random() * 8, w: mw, h: mh };
    m.renderOrder = 1;
    scene.add(m); S.mist.push(m);
  };
  mkMist(-6, 1.4, -20, 46, 6, 0.34, 0.014);
  mkMist(10, 1.9, -30, 58, 7.5, 0.28, 0.010);
  mkMist(-14, 2.3, -44, 70, 9, 0.24, 0.007);
  mkMist(22, 1.5, -14, 34, 5, 0.20, 0.017);
  mkMist(-30, 1.8, 6, 40, 6, 0.18, 0.011);

  /* ★ EFFET SIGNATURE N°1 — deux phares au loin : une chasse rentre */
  const hunt = new THREE.Group();
  const coreTex = texGlow('rgba(255,244,224,0.95)', 'rgba(255,180,90,0.5)');
  const haloTex = texGlow('rgba(255,190,110,0.5)', 'rgba(255,140,50,0.18)');
  const mkSprite = (t, s, ox) => {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false }));
    sp.scale.set(s, s, 1); sp.position.x = ox;
    hunt.add(sp); return sp;
  };
  mkSprite(coreTex, 1.05, -0.55);
  mkSprite(coreTex, 1.05, 0.55);
  const halo = mkSprite(haloTex, 5.2, 0);
  halo.position.y = 0.25;
  hunt.position.set(-80, 0.8, -55);
  scene.add(hunt);
  S.hunt = {
    g: hunt, t: REDUCED ? 0.42 : -0.25, dur: 44, pause: 13,
    p0: new THREE.Vector3(-88, 0.8, -58), p1: new THREE.Vector3(-14, 0.8, -34), p2: new THREE.Vector3(34, 0.8, -48),
  };
}

/* SITE++ — LES PINS PHOTO : billboards croisillon instanciés, 4 variantes réelles
   (pins sylvestres à contre-jour d'aube, Seedream + gate + rembg — le point CEO n°1).
   Même distribution radiale que les silhouettes, mêmes exclusions (trouée, corridor).
   alphaTest .5 : zéro tri, zéro voile. 4+1 draw calls remplacent 1 — assumé. */
const PINS_PHOTO = [
  { src: '/assets/img/pins/pin1.webp', ratio: 512 / 853 },
  { src: '/assets/img/pins/pin2.webp', ratio: 512 / 768 },
  { src: '/assets/img/pins/pin3.webp', ratio: 512 / 823 },
  { src: '/assets/img/pins/pin4.webp', ratio: 512 / 776 },
];
const PINS_NEAR = { src: '/assets/img/pins/pin2_near.webp', ratio: 768 / 1152 };
function loadPhotoPins() {
  if (S.pinsPhoto || (navigator.connection && navigator.connection.saveData)) return;
  const loader = new THREE.TextureLoader();
  const groups = [];
  let left = PINS_PHOTO.length + 1;
  const done = () => {
    if (--left > 0) return;
    groups.forEach((g) => S.scene.add(g));
    S.pinsPhoto = { groups, t0: performance.now() };
  };
  const mkTex = (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; return t; };
  const mkMat = (t) => new THREE.MeshBasicMaterial({
    map: mkTex(t), alphaTest: 0.5, side: THREE.DoubleSide,
    transparent: true, opacity: 0, depthWrite: true,
    color: 0x767888, // contre-jour : on assoit les silhouettes (assombrit, neutralise le bleu texture)
  });
  const quadCross = (ratio, h) => {
    const q1 = new THREE.PlaneGeometry(h * ratio, h);
    const q2 = q1.clone(); q2.rotateY(Math.PI / 2);
    return mergeGeoms([q1, q2]);
  };
  PINS_PHOTO.forEach((d, vi) => {
    loader.load(d.src, (tex) => {
      const H = 2.42; // hauteur du quad avant échelle (proche des 2.3 d'origine)
      const mesh = new THREE.InstancedMesh(quadCross(d.ratio, H), mkMat(tex), 22);
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), tv = new THREE.Vector3();
      let placed = 0, guard = 0;
      while (placed < 22 && guard++ < 900) {
        const a = Math.random() * Math.PI * 2;
        const r = 30 + Math.pow(Math.random(), 0.7) * 92;
        const px = Math.sin(a) * r, pz = Math.cos(a) * r;
        if (pz < -18 && pz > -75 && Math.abs(px) < 9) continue;   // la trouée vers l'aube
        if (pz > 8 && pz < 30 && Math.abs(px) < 14) continue;     // devant l'entrée
        /* lisière vraie : 60 % de sujets moyens, 40 % d'émergents — jamais un mur uniforme */
        const s = Math.random() < 0.6 ? 2.2 + Math.random() * 1.8 : 4.2 + Math.random() * 2.8;
        tv.set(px, (H / 2) * s - 0.03, pz);
        q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.random() * Math.PI);
        sc.set(s, s, s);
        m4.compose(tv, q, sc);
        mesh.setMatrixAt(placed++, m4);
      }
      mesh.count = placed;
      mesh.instanceMatrix.needsUpdate = true;
      groups.push(mesh);
      done();
    });
  });
  /* les trois du premier plan — le regard les frôle, la variante haute résolution */
  loader.load(PINS_NEAR.src, (tex) => {
    const H = 2.5;
    const mesh = new THREE.InstancedMesh(quadCross(PINS_NEAR.ratio, H), mkMat(tex), 3);
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), tv = new THREE.Vector3();
    [[-16.5, 14.5, 4.4, 0.6], [17.5, 9.5, 4.0, 2.2], [-13.5, -2.5, 4.6, 1.4]].forEach(([px, pz, s, ry], i) => {
      tv.set(px, (H / 2) * s - 0.03, pz);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry);
      sc.set(s, s, s);
      m4.compose(tv, q, sc);
      mesh.setMatrixAt(i, m4);
    });
    mesh.instanceMatrix.needsUpdate = true;
    groups.push(mesh);
    done();
  });
}

/* SITE++ — LES SOLS PBR : le canvas garde la COULEUR (identité, 0 octet),
   la photo apporte le RELIEF (normal) et la brillance variable (roughness)
   — c'est le rasant de l'aube qui révèle la taloche. Différé, 303 Ko. */
function loadPBRFloors() {
  if (S.pbrDone || (navigator.connection && navigator.connection.saveData)) return;
  S.pbrDone = true;
  const loader = new THREE.TextureLoader();
  let nor = null, rough = null, left = 2;
  const apply = () => {
    if (--left > 0) return;
    [[S.floorMat, 3, 3, 0.42, 0.9], [S.dalleMat, 2, 3, 0.3, 0.95]].forEach(([mat, rx, ry, ns, rg]) => {
      if (!mat) return;
      const n = nor.clone(); n.wrapS = n.wrapT = THREE.RepeatWrapping; n.repeat.set(rx, ry); n.needsUpdate = true;
      const r = rough.clone(); r.wrapS = r.wrapT = THREE.RepeatWrapping; r.repeat.set(rx, ry); r.needsUpdate = true;
      mat.normalMap = n; mat.normalScale = new THREE.Vector2(ns, ns);
      mat.roughnessMap = r; mat.roughness = rg;
      mat.needsUpdate = true;
    });
  };
  loader.load('/assets/img/pbr/beton_nor.webp', (t) => { t.anisotropy = 8; nor = t; apply(); });
  loader.load('/assets/img/pbr/beton_rough.webp', (t) => { t.anisotropy = 4; rough = t; apply(); });
}

/* SITE++ — LE CHEVALET (mixed-media à la Lando) : la PHOTO RÉELLE de l'occupant
   entre dans le monde 3D, posée sur un chevalet d'atelier — un outil de travail,
   pas une déco. La frame vient du spin360 déjà servi (zéro octet nouveau). */
function loadChevalet() {
  if (S.chevaletDone || !S.atelierGroup) return;
  S.chevaletDone = true;
  new THREE.TextureLoader().load('/assets/img/duster/spin360/frame_00.webp', (tex) => {
    tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
    const iw = tex.image.width || 4, ih = tex.image.height || 3;
    const PW = 0.92, PHt = PW * ih / iw;
    const g = new THREE.Group();
    const tilt = -0.11;
    const back = new THREE.Mesh(new THREE.BoxGeometry(PW + 0.1, PHt + 0.17, 0.024), MAT.bois);
    back.position.y = 1.26; back.rotation.x = tilt; back.castShadow = true; g.add(back);
    const photo = new THREE.Mesh(
      new THREE.PlaneGeometry(PW, PHt),
      new THREE.MeshBasicMaterial({ map: tex, color: 0xd6d2cc })
    );
    photo.position.set(0, 1.285, 0.0155); photo.rotation.x = tilt; g.add(photo);
    /* la mention qui fait la force : c'est une photo, et on le dit */
    const label = new THREE.Mesh(
      new THREE.PlaneGeometry(PW, 0.078),
      new THREE.MeshBasicMaterial({
        map: cnv(512, 44, (x, w, h) => {
          x.fillStyle = '#0d0c0a'; x.fillRect(0, 0, w, h);
          x.fillStyle = '#ff5b04'; x.font = F(700, 21);
          x.fillText('PHOTO RÉELLE · SANS RETOUCHE', 14, 30);
          x.fillStyle = '#8f867b'; x.font = FM(15);
          x.fillText('l’occupant du moment, vu par l’objectif', 322, 30);
        }),
      })
    );
    label.position.set(0, 1.285 - PHt / 2 - 0.075, 0.028); label.rotation.x = tilt; g.add(label);
    /* l'A-frame du chevalet : deux pieds avant, un pied arrière */
    [[-0.34, 0.075], [0.34, 0.075]].forEach(([lx, lz]) => {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.017, 0.021, 1.86, 8), MAT.bois);
      leg.position.set(lx, 0.93, lz); leg.rotation.x = -0.09; leg.castShadow = true; g.add(leg);
    });
    const legB = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.019, 1.78, 8), MAT.bois);
    legB.position.set(0, 0.88, -0.3); legB.rotation.x = 0.38; g.add(legB);
    const tray = new THREE.Mesh(new THREE.BoxGeometry(PW + 0.14, 0.03, 0.07), MAT.bois);
    tray.position.set(0, 1.26 - PHt / 2 - 0.115, 0.1); g.add(tray);
    g.position.set(2.35, 0, 2.85); g.rotation.y = -0.62;
    S.atelierGroup.add(g);
    addPick(g, 'occasion');
  });
}

/* petit utilitaire : merge de géométries planes (positions/uv/index) */
function mergeGeoms(geos) {
  let vCount = 0, iCount = 0;
  geos.forEach((g) => { vCount += g.attributes.position.count; iCount += g.index.count; });
  const posA = new Float32Array(vCount * 3), uvA = new Float32Array(vCount * 2), idxA = new Uint16Array(iCount);
  let vo = 0, io = 0;
  geos.forEach((g) => {
    posA.set(g.attributes.position.array, vo * 3);
    uvA.set(g.attributes.uv.array, vo * 2);
    const gi = g.index.array;
    for (let i = 0; i < gi.length; i++) idxA[io + i] = gi[i] + vo;
    vo += g.attributes.position.count; io += gi.length;
  });
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(posA, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uvA, 2));
  out.setIndex(new THREE.BufferAttribute(idxA, 1));
  return out;
}

/* ============================================================
   LE PAVILLON — verre, bois brûlé, béton ciré
   plan : baie vitrée plein nord (z=-7, l'aube derrière),
   mur des mandats à l'ouest (x=-11), atelier vitré à l'est (x>+5)
   ============================================================ */
const matBois = () => new THREE.MeshStandardMaterial({ map: texBoisBrule(), color: 0xffffff, roughness: 0.58, metalness: 0.12 });
let MAT = {};

function buildPavillon(scene) {
  MAT.bois = matBois();
  MAT.boisSol = new THREE.MeshStandardMaterial({ color: 0x181310, roughness: 0.8, metalness: 0.05 });
  MAT.metal = new THREE.MeshStandardMaterial({ color: 0x14120f, roughness: 0.42, metalness: 0.72 });
  MAT.laiton = new THREE.MeshStandardMaterial({ color: 0x9a7b3c, roughness: 0.32, metalness: 0.95, envMapIntensity: 1.3 });
  MAT.verre = new THREE.MeshPhysicalMaterial({
    color: 0xa8c4d4, metalness: 0, roughness: 0.05, transparent: true, opacity: 0.08,
    envMapIntensity: 1.7, side: THREE.DoubleSide, depthWrite: false,
  });
  MAT.verreDepoli = new THREE.MeshPhysicalMaterial({
    color: 0xcfd8dc, metalness: 0, roughness: 0.55, transparent: true, opacity: 0.16,
    envMapIntensity: 0.9, side: THREE.DoubleSide, depthWrite: false,
  });
  MAT.cuir = new THREE.MeshStandardMaterial({ color: 0x241a12, roughness: 0.62, metalness: 0.04 });

  /* sol : béton ciré qui accroche l'aube */
  S.floorMat = new THREE.MeshStandardMaterial({ map: texBeton(), roughness: 0.3, metalness: 0.18, envMapIntensity: 1.15 });
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(22.8, 14.8),
    S.floorMat
  );
  floor.rotation.x = -Math.PI / 2; floor.position.y = 0.02; floor.receiveShadow = true;
  scene.add(floor);

  /* socle béton (le pavillon est posé sur sa dalle) */
  const slab = new THREE.Mesh(new THREE.BoxGeometry(24.4, 0.34, 16.4), new THREE.MeshStandardMaterial({ color: 0x1d1a17, roughness: 0.85 }));
  slab.position.y = -0.17; scene.add(slab);

  /* toit + sous-face bois */
  const roof = new THREE.Mesh(new THREE.BoxGeometry(25.6, 0.36, 17.6), MAT.metal);
  roof.position.y = 4.28; roof.castShadow = true; scene.add(roof);
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(24.6, 16.6), MAT.boisSol);
  ceil.rotation.x = Math.PI / 2; ceil.position.y = 4.09; scene.add(ceil);
  /* lattis plafond (instancié) */
  const latte = new THREE.InstancedMesh(new THREE.BoxGeometry(0.1, 0.07, 15.6), MAT.bois, 23);
  const lm = new THREE.Matrix4();
  for (let i = 0; i < 23; i++) {
    lm.setPosition(-11 + i * 1.0, 4.03, 0);
    latte.setMatrixAt(i, lm);
  }
  latte.instanceMatrix.needsUpdate = true;
  scene.add(latte);

  /* murs pleins : ouest (mur des mandats) + sud (entrée) */
  const wallW = new THREE.Mesh(new THREE.BoxGeometry(0.24, 4.2, 14.8), MAT.bois);
  wallW.position.set(-11.3, 2.1, 0); scene.add(wallW);
  const wallS1 = new THREE.Mesh(new THREE.BoxGeometry(8.6, 4.2, 0.24), MAT.bois);
  wallS1.position.set(-7.0, 2.1, 7.3); scene.add(wallS1);
  const wallS2 = new THREE.Mesh(new THREE.BoxGeometry(8.2, 4.2, 0.24), MAT.bois);
  wallS2.position.set(7.2, 2.1, 7.3); scene.add(wallS2);
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(5.5, 1.15, 0.24), MAT.bois);
  lintel.position.set(0.1, 3.63, 7.3); scene.add(lintel); // linteau au-dessus de l'entrée (porte ouverte sur la nuit qui finit)

  /* LA BAIE VITRÉE nord — l'aube entre ici */
  const bay = new THREE.Mesh(new THREE.PlaneGeometry(22.6, 3.9), MAT.verre);
  bay.position.set(0, 2.05, -7.28); bay.renderOrder = 3;
  scene.add(bay);
  /* baie est (atelier ouvert sur la clairière) */
  const bayE = new THREE.Mesh(new THREE.PlaneGeometry(14.6, 3.9), MAT.verre);
  bayE.rotation.y = -Math.PI / 2; bayE.position.set(11.3, 2.05, 0); bayE.renderOrder = 3;
  scene.add(bayE);

  /* meneaux bois brûlé (instanciés) — leurs ombres rayent le sol à l'aube */
  const mull = new THREE.InstancedMesh(new THREE.BoxGeometry(0.13, 4.14, 0.16), MAT.bois, 18);
  const mm = new THREE.Matrix4(); const mq = new THREE.Quaternion(); const ms = new THREE.Vector3(1, 1, 1);
  let mi = 0;
  for (let i = 0; i <= 10; i++) { // baie nord
    mm.compose(new THREE.Vector3(-11.3 + i * 2.26, 2.07, -7.28), mq, ms);
    mull.setMatrixAt(mi++, mm);
  }
  mq.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
  for (let i = 0; i <= 6; i++) { // baie est
    mm.compose(new THREE.Vector3(11.3, 2.07, -7.3 + i * 2.44), mq, ms);
    mull.setMatrixAt(mi++, mm);
  }
  mull.count = mi;
  mull.instanceMatrix.needsUpdate = true;
  mull.castShadow = true;
  scene.add(mull);
  /* traverses hautes/basses */
  const rails = new THREE.InstancedMesh(new THREE.BoxGeometry(22.8, 0.12, 0.16), MAT.bois, 2);
  const rm = new THREE.Matrix4();
  rm.setPosition(0, 0.12, -7.28); rails.setMatrixAt(0, rm);
  rm.setPosition(0, 4.02, -7.28); rails.setMatrixAt(1, rm);
  rails.instanceMatrix.needsUpdate = true;
  scene.add(rails);

  /* cloison vitrée de L'ATELIER (x=+5, de z=-7 à z=+1.6 ; dépoli côté fond) */
  const part1 = new THREE.Mesh(new THREE.PlaneGeometry(4.4, 3.9), MAT.verre);
  part1.rotation.y = Math.PI / 2; part1.position.set(5, 2.05, -5.1); part1.renderOrder = 3;
  scene.add(part1);
  const part2 = new THREE.Mesh(new THREE.PlaneGeometry(5.8, 3.9), MAT.verreDepoli);
  part2.rotation.y = Math.PI / 2; part2.position.set(5, 2.05, -0.1); part2.renderOrder = 3;
  scene.add(part2); // ← le verre dépoli : c'est derrière lui que passe la Présence
  const partPost = new THREE.InstancedMesh(new THREE.BoxGeometry(0.12, 4.1, 0.14), MAT.bois, 4);
  const pm = new THREE.Matrix4();
  [-7.2, -2.9, 2.8, 1.6].forEach((pz, i) => { pm.setPosition(5, 2.06, pz); partPost.setMatrixAt(i, pm); });
  partPost.count = 4;
  partPost.instanceMatrix.needsUpdate = true;
  scene.add(partPost);

  /* poutres structurelles */
  const beams = new THREE.InstancedMesh(new THREE.BoxGeometry(0.22, 0.3, 15.2), MAT.bois, 5);
  const bm = new THREE.Matrix4();
  for (let i = 0; i < 5; i++) { bm.setPosition(-9 + i * 4.5, 3.9, 0); beams.setMatrixAt(i, bm); }
  beams.instanceMatrix.needsUpdate = true;
  scene.add(beams);

  /* les rais de l'aube — faux volumes rasants qui entrent par la baie */
  const shaftTex = (() => {
    const c = document.createElement('canvas'); c.width = 256; c.height = 64;
    const x = c.getContext('2d');
    const g = x.createLinearGradient(0, 0, 256, 0);
    g.addColorStop(0, 'rgba(255,170,90,0.55)');
    g.addColorStop(0.5, 'rgba(255,160,80,0.16)');
    g.addColorStop(1, 'rgba(255,150,70,0)');
    x.fillStyle = g; x.fillRect(0, 0, 256, 64);
    return new THREE.CanvasTexture(c);
  })();
  [[-3.2, 1.4], [1.4, 1.1], [5.9, 1.7], [-7.6, 1.2]].forEach(([sx, sy]) => {
    const sh = new THREE.Mesh(
      new THREE.PlaneGeometry(9.5, 1.5),
      new THREE.MeshBasicMaterial({ map: shaftTex, transparent: true, opacity: 0.055, blending: THREE.AdditiveBlending, depthWrite: false, fog: false, side: THREE.DoubleSide })
    );
    sh.position.set(sx + 1.05, sy, -2.4);
    sh.rotation.y = Math.PI / 2 - Math.atan2(SUN_DIR.x, -SUN_DIR.z);
    sh.rotation.z = -0.055;
    sh.renderOrder = 4;
    scene.add(sh); S.shafts.push(sh);
  });
}

/* ============================================================
   LES PIÈCES
   ============================================================ */

/* — LE BUREAU DU CHASSEUR (le centre, recadrage CEO n°4) — */
function buildBureau(scene, tex) {
  const g = new THREE.Group(); g.position.set(0, 0, -2.6);

  /* la grande table : plateau chêne massif brut, piètement acier */
  const top = new THREE.Mesh(new THREE.BoxGeometry(2.7, 0.09, 1.15), new THREE.MeshStandardMaterial({ color: 0x2c2318, roughness: 0.5, metalness: 0.06 }));
  top.position.y = 0.76; top.castShadow = true; g.add(top);
  [[-1.22, 0], [1.22, 0]].forEach(([lx]) => {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.74, 1.0), MAT.metal);
    leg.position.set(lx, 0.37, 0); leg.castShadow = true; g.add(leg);
  });

  /* l'écran du chasseur, allumé : le configurateur */
  const scrFrame = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.66, 0.05), MAT.metal);
  scrFrame.position.set(0, 1.28, -0.28); scrFrame.rotation.x = -0.07; g.add(scrFrame);
  const scr = new THREE.Mesh(new THREE.PlaneGeometry(0.97, 0.59), new THREE.MeshBasicMaterial({ map: tex.config }));
  scr.position.set(0, 1.28, -0.25); scr.rotation.x = -0.07; g.add(scr);
  S.screenBlink.push(scr);
  const foot = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.5, 0.07), MAT.metal);
  foot.position.set(0, 0.98, -0.33); g.add(foot);
  const kb = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.024, 0.2), MAT.metal);
  kb.position.set(0, 0.82, 0.05); g.add(kb);

  /* la lampe d'architecte, allumée avant le jour */
  const lampArm = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.62, 8), MAT.metal);
  lampArm.position.set(-0.95, 1.06, 0.1); lampArm.rotation.z = 0.5; g.add(lampArm);
  const lampHead = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.15, 14, 1, true), new THREE.MeshStandardMaterial({ color: 0x111010, roughness: 0.4, metalness: 0.7, side: THREE.DoubleSide, emissive: 0xffb877, emissiveIntensity: 0.55 }));
  lampHead.position.set(-1.1, 1.3, 0.12); lampHead.rotation.z = 2.4; g.add(lampHead);
  const lampLight = new THREE.PointLight(0xffb877, 3.2, 4.6, 2);
  lampLight.position.set(-1.05, 1.2, 0.15); g.add(lampLight);

  /* dossiers et tasse — la vie du poste */
  const pile = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.05, 0.25), new THREE.MeshStandardMaterial({ color: 0x3a2f24, roughness: 0.85 }));
  pile.position.set(0.85, 0.833, 0.22); pile.rotation.y = -0.2; g.add(pile);
  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.034, 0.09, 14), new THREE.MeshStandardMaterial({ color: 0xcfc8be, roughness: 0.35 }));
  cup.position.set(0.55, 0.85, -0.28); g.add(cup);

  /* le fauteuil du chasseur (dos à la baie) + les deux fauteuils clients */
  const chair = (cx, cz, ry, mat) => {
    const c = new THREE.Group();
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.08, 0.5), mat); seat.position.y = 0.46; c.add(seat);
    const back = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.55, 0.08), mat); back.position.set(0, 0.78, -0.24); c.add(back);
    const pied = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.44, 10), MAT.metal); pied.position.y = 0.22; c.add(pied);
    c.position.set(cx, 0, cz); c.rotation.y = ry;
    c.traverse((o) => { if (o.isMesh) o.castShadow = true; });
    g.add(c);
  };
  chair(0, -1.05, 0, MAT.cuir);
  chair(-0.55, 1.05, Math.PI + 0.24, MAT.cuir);
  chair(0.55, 1.05, Math.PI - 0.24, MAT.cuir);

  /* la carte du terrain de chasse (Europe) épinglée — panneau sur pied derrière le bureau */
  const board = new THREE.Group(); board.position.set(-2.2, 0, -1.55); board.rotation.y = 0.42;
  const bFrame = new THREE.Mesh(new THREE.BoxGeometry(1.75, 1.34, 0.06), MAT.bois);
  bFrame.position.y = 1.62; bFrame.castShadow = true; board.add(bFrame);
  const bMap = new THREE.Mesh(new THREE.PlaneGeometry(1.66, 1.25), new THREE.MeshBasicMaterial({ map: tex.carte }));
  bMap.position.set(0, 1.62, 0.035); board.add(bMap);
  [[-0.6], [0.6]].forEach(([bx]) => {
    const pied = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.026, 0.98, 8), MAT.metal);
    pied.position.set(bx, 0.49, 0); board.add(pied);
  });
  g.add(board);
  S.pinBoard = board; // v3.2 : l'avis de recherche scellé vient s'épingler ici

  scene.add(g);
  addPick(g, 'bureau');
}

/* — LA TABLE DES PREUVES — */
function buildPreuves(scene) {
  const g = new THREE.Group(); g.position.set(-5.7, 0, -3.7); g.rotation.y = 0.5;
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.75, 0.07, 1.05), new THREE.MeshStandardMaterial({ color: 0x241d15, roughness: 0.55 }));
  top.position.y = 0.72; top.castShadow = true; g.add(top);
  [[-0.78, -0.42], [0.78, -0.42], [-0.78, 0.42], [0.78, 0.42]].forEach(([lx, lz]) => {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.72, 0.05), MAT.metal);
    leg.position.set(lx, 0.36, lz); g.add(leg);
  });
  /* trois dossiers ouverts : la ligne réelle + deux fantômes honnêtes */
  [0, 1, 2].forEach((n) => {
    const d = new THREE.Mesh(new THREE.PlaneGeometry(0.52, 0.37), new THREE.MeshBasicMaterial({ map: texDossier(n) }));
    d.rotation.x = -Math.PI / 2; d.rotation.z = (n - 1) * 0.34 + (Math.random() - 0.5) * 0.06;
    d.position.set((n - 1) * 0.55, 0.762, n === 1 ? -0.08 : 0.1);
    g.add(d);
  });
  /* la loupe de laiton posée sur la ligne réelle */
  const loupe = new THREE.Group();
  const anneau = new THREE.Mesh(new THREE.TorusGeometry(0.085, 0.012, 10, 28), MAT.laiton);
  anneau.rotation.x = Math.PI / 2; loupe.add(anneau);
  const manche = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.015, 0.16, 8), MAT.laiton);
  manche.rotation.z = Math.PI / 2 - 0.5; manche.position.set(0.145, 0, 0.06); loupe.add(manche);
  loupe.position.set(-0.62, 0.78, 0.05); loupe.rotation.y = 0.6;
  g.add(loupe);
  /* pile de dossiers fermés */
  const pile = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.09, 0.3), new THREE.MeshStandardMaterial({ color: 0x352a1e, roughness: 0.85 }));
  pile.position.set(0.6, 0.8, -0.3); pile.rotation.y = 0.3; g.add(pile);
  /* la suspension au-dessus des preuves — on lit sous la lampe */
  const fil = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 1.35, 6), MAT.metal);
  fil.position.set(0, 3.35, 0); g.add(fil);
  const abat = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.16, 16, 1, true), new THREE.MeshStandardMaterial({ color: 0x131210, roughness: 0.45, metalness: 0.6, side: THREE.DoubleSide, emissive: 0xffc188, emissiveIntensity: 0.5 }));
  abat.position.set(0, 2.72, 0); g.add(abat);
  const pl = new THREE.PointLight(0xffc188, 3.0, 4.6, 2);
  pl.position.set(0, 2.5, 0); g.add(pl);
  scene.add(g);
  addPick(g, 'preuves');
}

/* — LE MUR DES MANDATS (un seul draw call pour les 15 fiches) — */
function buildMandats(scene, tex) {
  const wall = new THREE.Mesh(new THREE.PlaneGeometry(6.6, 3.3), new THREE.MeshBasicMaterial({ map: tex.mandats }));
  wall.position.set(-11.17, 2.0, -2.0); wall.rotation.y = Math.PI / 2;
  scene.add(wall);
  addPick(wall, 'galerie');
  /* réglette lumineuse au-dessus (émissive, zéro vraie lumière) */
  const regle = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 6.4), new THREE.MeshStandardMaterial({ color: 0x111010, emissive: 0xffc990, emissiveIntensity: 1.6 }));
  regle.position.set(-11.05, 3.78, -2.0);
  scene.add(regle);
}

/* — L'ATELIER VITRÉ : le pont, l'occasion du moment, la Révélation — */
function buildAtelier(scene, tex, occ) {
  const g = new THREE.Group(); g.position.set(8.15, 0, -2.7);

  /* dalle atelier légèrement différente (résine claire) */
  S.dalleMat = new THREE.MeshStandardMaterial({ color: 0x211f1e, roughness: 0.38, metalness: 0.2, envMapIntensity: 1.0 });
  const dalle = new THREE.Mesh(new THREE.PlaneGeometry(5.6, 8.6), S.dalleMat);
  dalle.rotation.x = -Math.PI / 2; dalle.position.set(0.35, 0.035, 0.35); dalle.receiveShadow = true; g.add(dalle);

  /* LE PONT ÉLÉVATEUR deux colonnes */
  const colMat = new THREE.MeshStandardMaterial({ color: 0x23211e, roughness: 0.5, metalness: 0.6 });
  [[-1.62], [1.62]].forEach(([cx]) => {
    const col = new THREE.Mesh(new THREE.BoxGeometry(0.34, 2.7, 0.3), colMat);
    col.position.set(cx, 1.35, 0); col.castShadow = true; g.add(col);
    const pied = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.08, 0.5), colMat);
    pied.position.set(cx, 0.06, 0); g.add(pied);
    const chariot = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.34), MAT.metal);
    chariot.position.set(cx, 1.0, 0); g.add(chariot);
    /* bras vers les rails */
    [[-0.5], [0.5]].forEach(([bz]) => {
      const bras = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.07, 0.12), MAT.metal);
      bras.position.set(cx * 0.62, 1.06, bz * 1.5); g.add(bras);
    });
  });
  /* rails sous la voiture */
  [[-0.56], [0.56]].forEach(([rx]) => {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.09, 3.9), colMat);
    rail.position.set(rx, 1.1, 0); rail.castShadow = true; g.add(rail);
  });

  /* LA VOITURE — l'occupant du moment (teinte pilotée par occasion.json) */
  const paint = parseInt(occ.peinture3d, 16) || 0xe8560e;
  const car = buildCar(paint);
  car.position.set(0, 1.145, 0);
  car.rotation.y = -Math.PI / 2; // nez vers l'entrée : trois-quarts avant depuis le hall
  g.add(car);
  S.atelierGroup = g; // SITE++ : le chevalet mixed-media s'y accroche en différé

  /* la plaque de l'atelier — « l'occasion du moment » */
  const plaque = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 0.85), new THREE.MeshBasicMaterial({ map: tex.plaqueAtelier }));
  plaque.position.set(0, 3.35, -3.9); g.add(plaque);

  /* l'éclairage d'atelier : froid, contraste avec le hall chaud */
  const stripMat = new THREE.MeshStandardMaterial({ color: 0x0f0f10, emissive: 0xdfe8ff, emissiveIntensity: 1.9 });
  const strip = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.04, 3.4), stripMat);
  strip.position.set(0, 3.72, 0); g.add(strip);
  const atelierLight = new THREE.PointLight(0xdfe8ff, 6.0, 9.5, 2);
  atelierLight.position.set(0, 3.3, 0); g.add(atelierLight);

  /* ★ EFFET SIGNATURE N°2 — la RÉVÉLATION HONNÊTE
     5 faisceaux d'inspection qui s'illuminent en séquence quand l'IA parle */
  const zoneDefs = [ // en coordonnées locales voiture (x = longueur, nez +x)
    [1.55, 1.02, 0],    // 0 : le moteur — ce qui cloche d'abord
    [0.15, 1.32, 0.55], // 1 : le carnet / l'habitacle
    [0.55, 1.18, -0.5], // 2 : le compteur
    [-0.6, 1.66, 0],    // 3 : la carrosserie entière (reconditionnement)
    [2.35, 0.55, 0],    // 4 : le prix (l'avant + le pont)
  ];
  const beamTex = (() => {
    const c = document.createElement('canvas'); c.width = 64; c.height = 256;
    const x = c.getContext('2d');
    const gr = x.createLinearGradient(0, 0, 0, 256);
    gr.addColorStop(0, 'rgba(255,214,160,0.85)');
    gr.addColorStop(1, 'rgba(255,180,110,0)');
    x.fillStyle = gr; x.fillRect(0, 0, 64, 256);
    return new THREE.CanvasTexture(c);
  })();
  zoneDefs.forEach(([zx, zy, zz], i) => {
    /* l'orbe sur la zone */
    const orb = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 14, 14),
      new THREE.MeshStandardMaterial({ color: ACCENT, emissive: ACCENT, emissiveIntensity: 0, transparent: true, opacity: 0.9 })
    );
    orb.position.set(zx, zy, zz);
    car.add(orb);
    const zl = new THREE.PointLight(ACCENT, 0, 2.8, 2);
    zl.position.set(zx, zy + 0.15, zz);
    car.add(zl);
    /* la lampe d'inspection au plafond + son faisceau */
    const world = new THREE.Vector3(zx, zy, zz);
    car.localToWorld(world); g.worldToLocal(world);
    const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 0.12, 12), MAT.metal);
    lamp.position.set(world.x, 3.66, world.z); g.add(lamp);
    const beamH = 3.6 - world.y;
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.5, beamH, 18, 1, true),
      new THREE.MeshBasicMaterial({ map: beamTex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false })
    );
    beam.position.set(world.x, 3.6 - beamH / 2, world.z);
    beam.renderOrder = 5;
    g.add(beam);
    S.zones.push({ orb, light: zl });
    S.cones.push(beam);
  });

  scene.add(g);
  addPick(g, 'occasion');
  return g;
}

/* la silhouette SUV de l'occupant — teinte paramétrée, jamais de fausse 3D photoréaliste */
function buildCar(paintColor) {
  const car = new THREE.Group(); car.name = 'occasion';
  const body = new THREE.Shape();
  body.moveTo(-2.06, 0.44);
  body.lineTo(-1.95, 0.36);
  body.lineTo(-1.82, 0.36);
  body.absarc(-1.32, 0.36, 0.5, Math.PI, 0, true);
  body.lineTo(0.84, 0.36);
  body.absarc(1.34, 0.36, 0.5, Math.PI, 0, true);
  body.lineTo(2.02, 0.36);
  body.lineTo(2.16, 0.5);
  body.lineTo(2.2, 0.86);
  body.quadraticCurveTo(2.14, 1.0, 1.72, 1.05);
  body.lineTo(0.95, 1.1);
  body.quadraticCurveTo(0.62, 1.13, 0.36, 1.38);
  body.quadraticCurveTo(0.12, 1.58, -0.3, 1.62);
  body.lineTo(-1.38, 1.6);
  body.quadraticCurveTo(-1.72, 1.58, -1.86, 1.32);
  body.lineTo(-2.0, 0.95);
  body.closePath();
  const bodyGeo = new THREE.ExtrudeGeometry(body, { depth: 1.5, bevelEnabled: true, bevelThickness: 0.12, bevelSize: 0.1, bevelSegments: 3, curveSegments: 20 });
  bodyGeo.translate(0, 0, -0.75);
  S.paintMat = new THREE.MeshPhysicalMaterial({
    color: paintColor, metalness: 0.42, roughness: 0.3,
    clearcoat: 1, clearcoatRoughness: 0.14, envMapIntensity: 1.35,
    emissive: ACCENT, emissiveIntensity: 0,
  });
  const bodyMesh = new THREE.Mesh(bodyGeo, S.paintMat);
  bodyMesh.castShadow = true; car.add(bodyMesh);

  const win = new THREE.Shape();
  win.moveTo(0.62, 1.08);
  win.quadraticCurveTo(0.4, 1.12, 0.18, 1.35);
  win.quadraticCurveTo(0.02, 1.51, -0.32, 1.54);
  win.lineTo(-1.36, 1.52);
  win.quadraticCurveTo(-1.6, 1.5, -1.7, 1.3);
  win.lineTo(-1.52, 1.13);
  win.closePath();
  const winGeo = new THREE.ExtrudeGeometry(win, { depth: 1.78, bevelEnabled: false });
  winGeo.translate(0, 0, -0.89);
  const glass = new THREE.MeshPhysicalMaterial({ color: 0x0b0e12, metalness: 0.85, roughness: 0.12, envMapIntensity: 1.6 });
  car.add(new THREE.Mesh(winGeo, glass));

  const tire = new THREE.MeshStandardMaterial({ color: 0x121110, roughness: 0.92 });
  const rim = new THREE.MeshStandardMaterial({ color: 0x9a938c, metalness: 0.92, roughness: 0.22, envMapIntensity: 1.3 });
  [[1.34, 0.78], [1.34, -0.78], [-1.32, 0.78], [-1.32, -0.78]].forEach(([wx, wz]) => {
    const t = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.36, 0.26, 28), tire);
    t.rotation.x = Math.PI / 2; t.position.set(wx, 0.36, wz); t.castShadow = true; car.add(t);
    const r = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.27, 20), rim);
    r.rotation.x = Math.PI / 2; r.position.set(wx, 0.36, wz); car.add(r);
  });
  const black = new THREE.MeshStandardMaterial({ color: 0x121110, roughness: 0.7 });
  const grille = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.3, 1.15), black);
  grille.position.set(2.19, 0.68, 0); car.add(grille);
  const lampMat = new THREE.MeshStandardMaterial({ color: 0xfff4e0, emissive: 0xffd9a8, emissiveIntensity: 1.4 });
  [[0.5], [-0.5]].forEach(([lz]) => {
    const l = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.13, 0.34), lampMat);
    l.position.set(2.2, 0.93, lz); car.add(l);
  });
  const tailMat = new THREE.MeshStandardMaterial({ color: 0x551108, emissive: 0xff2412, emissiveIntensity: 0.8 });
  [[0.62], [-0.62]].forEach(([lz]) => {
    const l = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.24, 0.2), tailMat);
    l.position.set(-2.04, 1.05, lz); car.add(l);
  });
  [[0.62], [-0.62]].forEach(([rz]) => {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.05, 0.07), black);
    rail.position.set(-0.55, 1.68, rz); car.add(rail);
  });
  [[0.94], [-0.94]].forEach(([mz]) => {
    const mir = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.1, 0.16), black);
    mir.position.set(0.68, 1.16, mz); car.add(mir);
  });
  car.scale.setScalar(0.94);
  return car;
}

/* — LE TERMINAL DU PROUVEUR — */
function buildProuveur(scene, tex) {
  const g = new THREE.Group(); g.position.set(3.15, 0, 2.75); g.rotation.y = -0.9;
  const ped = new THREE.Mesh(new THREE.BoxGeometry(0.54, 1.04, 0.44), MAT.bois);
  ped.position.y = 0.52; ped.castShadow = true; g.add(ped);
  const tFrame = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.52, 0.05), MAT.metal);
  tFrame.position.set(0, 1.24, 0.02); tFrame.rotation.x = -0.42; g.add(tFrame);
  const tScr = new THREE.Mesh(new THREE.PlaneGeometry(0.68, 0.46), new THREE.MeshBasicMaterial({ map: tex.prouveur }));
  tScr.position.set(0, 1.253, 0.055); tScr.rotation.x = -0.42; g.add(tScr);
  S.screenBlink.push(tScr);
  /* lampe d'enquêteur au-dessus */
  const fil = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, 1.1, 6), MAT.metal);
  fil.position.set(0, 3.4, 0); g.add(fil);
  const abat = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.14, 16, 1, true), new THREE.MeshStandardMaterial({ color: 0x131210, roughness: 0.45, metalness: 0.6, side: THREE.DoubleSide, emissive: 0xffc188, emissiveIntensity: 0.5 }));
  abat.position.set(0, 2.86, 0); g.add(abat);
  const pl = new THREE.PointLight(0xffb877, 2.2, 4.4, 2);
  pl.position.set(0, 2.7, 0); g.add(pl);
  scene.add(g);
  addPick(g, 'prouveur');
}

/* — ★ EFFET SIGNATURE N°3 : LA PRÉSENCE DU CHASSEUR —
   un manteau sur la patère (clic → le manifeste), une silhouette
   qui passe derrière le verre dépoli. Jamais un visage. */
function buildPresence(scene, tex) {
  /* la patère et le manteau */
  const g = new THREE.Group(); g.position.set(-4.5, 0, 3.6);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.03, 1.78, 10), MAT.bois);
  pole.position.y = 0.89; pole.castShadow = true; g.add(pole);
  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.22, 0.04, 14), MAT.metal);
  base.position.y = 0.02; g.add(base);
  [[0.3, 1.62], [-0.28, 1.55], [0.05, 1.68]].forEach(([hx, hy]) => {
    const hook = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.3, 8), MAT.laiton);
    hook.rotation.z = Math.PI / 2 - 0.35; hook.position.set(hx * 0.6, hy, 0.02); g.add(hook);
  });
  /* le manteau du chasseur — toile cirée sombre */
  const coatPts = [];
  coatPts.push(new THREE.Vector2(0.035, 1.52));
  coatPts.push(new THREE.Vector2(0.2, 1.4));
  coatPts.push(new THREE.Vector2(0.235, 1.24));
  coatPts.push(new THREE.Vector2(0.2, 1.0));
  coatPts.push(new THREE.Vector2(0.24, 0.72));
  coatPts.push(new THREE.Vector2(0.26, 0.58));
  coatPts.push(new THREE.Vector2(0.0, 0.56));
  const coat = new THREE.Mesh(new THREE.LatheGeometry(coatPts, 22), new THREE.MeshStandardMaterial({ color: 0x1c1812, roughness: 0.88, metalness: 0.02, side: THREE.DoubleSide }));
  coat.scale.z = 0.55; coat.position.set(0.16, 0.06, 0.05); coat.castShadow = true;
  g.add(coat);
  /* le chapeau */
  const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.155, 0.165, 0.018, 18), new THREE.MeshStandardMaterial({ color: 0x181410, roughness: 0.9 }));
  brim.position.set(0.02, 1.72, 0.02); brim.rotation.z = 0.1; g.add(brim);
  const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.1, 0.11, 16), brim.material);
  crown.position.set(0.02, 1.78, 0.02); crown.rotation.z = 0.1; g.add(crown);
  /* l'applique de l'entrée — la Présence se devine à contre-nuit */
  const app = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.05, 0.08), new THREE.MeshStandardMaterial({ color: 0x111010, emissive: 0xffc188, emissiveIntensity: 1.4 }));
  app.position.set(0.3, 2.5, -0.55); g.add(app);
  const appLight = new THREE.PointLight(0xffbf85, 2.4, 4.2, 2);
  appLight.position.set(0.3, 2.4, -0.5); g.add(appLight);
  scene.add(g);
  addPick(g, 'manifeste');

  /* la silhouette derrière le verre dépoli de l'atelier */
  const sil = new THREE.Mesh(
    new THREE.PlaneGeometry(1.0, 2.0),
    new THREE.MeshBasicMaterial({ map: tex.silhouette, transparent: true, opacity: 0, depthWrite: false, color: 0x090910 })
  );
  sil.position.set(5.55, 1.02, 1.2); // à L'INTÉRIEUR de l'atelier, derrière le dépoli
  sil.rotation.y = -Math.PI / 2;
  sil.renderOrder = 2;
  scene.add(sil);
  S.passer = { mesh: sil, next: REDUCED ? Infinity : 14, active: false, t0: 0, dur: 15, from: 1.4, to: -4.9 };
}

function addPick(group, id) {
  if (group.isMesh) { group.userData.station = id; S.pickables.push(group); return; }
  group.traverse((o) => { if (o.isMesh) { o.userData.station = id; S.pickables.push(o); } });
}

/* ============================================================
   PANNEAUX (contenus v1/v2 réutilisés — les textes aimés)
   ============================================================ */
function openStation(id) {
  if (S.focused === id) return;
  if (S.focused) { // bascule directe de pièce à pièce
    stopRevelation();
    dom.panel.classList.remove('open');
    dom.panel.setAttribute('aria-hidden', 'true');
    S.focused = null;
  }
  S.focused = id;
  sound.click();
  dom.entry.classList.add('hide');
  dom.hint.classList.add('hide');
  syncRoomsMenu();
  if (window.innerWidth > 720) {
    const w = window.innerWidth, h = window.innerHeight;
    S.camera.setViewOffset(w, h, Math.min(275, w * 0.22), 0, w, h);
  }
  tweenTo(VIEWS[id], 1.7, () => { rig.mode = 'focus'; });
  setTimeout(() => openPanel(id), REDUCED ? 60 : 750);
}
function openPanel(id) {
  dom.panelC.innerHTML = '';
  if (id === 'occasion') {
    /* P2 : enfants étalés (pas un wrapper unique) pour que le stagger CSS opère */
    dom.panelC.append(...[...buildOccasionPanel(S.occ).children]);
  } else {
    const tpl = document.getElementById('tpl-' + id);
    if (!tpl) return;
    dom.panelC.append(tpl.content.cloneNode(true));
  }
  dom.panelC.scrollTop = 0;
  wirePanel(id, dom.panelC);
  dom.panel.classList.add('open');
  dom.panel.setAttribute('aria-hidden', 'false');
  dom.panelC.focus({ preventScroll: true });
}
function closePanel() {
  stopRevelation();
  dom.panel.classList.remove('open');
  dom.panel.setAttribute('aria-hidden', 'true');
  if (!S.focused) return;
  S.focused = null;
  syncRoomsMenu();
  S.camera.clearViewOffset();
  sound.blip(240, 0.08, 0.03);
  tweenTo(VIEWS.orbit, 1.4, () => { rig.mode = 'orbit'; });
  dom.hint.classList.remove('hide');
}
dom.close.addEventListener('click', closePanel);
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closePanel();
  const n = parseInt(e.key, 10);
  const ids = Object.keys(STATIONS);
  if (n >= 1 && n <= ids.length && !e.ctrlKey && !e.metaKey && !e.altKey && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'SELECT' && document.activeElement.tagName !== 'TEXTAREA') {
    openStation(ids[n - 1]);
  }
});

/* le menu des pièces (navigation de secours + clavier + mobile) */
function buildRoomsMenu() {
  Object.entries(STATIONS).forEach(([id, st], i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.station = id;
    b.innerHTML = `<i>${i + 1}</i>${st.menu}`;
    b.title = st.label;
    b.setAttribute('aria-label', st.label);
    b.addEventListener('click', () => openStation(id));
    dom.rooms.append(b);
  });
}
function syncRoomsMenu() {
  dom.rooms.querySelectorAll('button').forEach((b) => {
    const on = b.dataset.station === S.focused;
    b.classList.toggle('on', on);
    if (on) b.setAttribute('aria-current', 'true');
    else b.removeAttribute('aria-current');
  });
}

/* le panneau OCCASION, généré depuis occasion.json (le slot) */
function buildOccasionPanel(occ) {
  const root = document.createElement('div');
  const specRows = occ.specs.map(([k, v]) => `<div><span>${k}</span><b${k === 'Teinte' ? ' class="accent"' : ''}>${v}</b></div>`).join('');
  const photos = `
    <figure class="shots">
      <img src="${occ.photos[0].src}" alt="${occ.photos[0].alt}" loading="lazy" />
      <div class="shots-row">
        ${occ.photos.slice(1).map((p) => `<img src="${p.src}" alt="${p.alt}" loading="lazy" />`).join('')}
      </div>
      <figcaption>${occ.creditPhotos}</figcaption>
    </figure>
    <figure class="shots ambiances">
      <div class="ambiance-grid">
        ${occ.ambiances.map((p) => `<img src="${p.src}" alt="${p.alt}" loading="lazy" />`).join('')}
      </div>
      <video class="teaser" src="${occ.video.src}" poster="${occ.video.poster}" controls muted loop playsinline preload="none" aria-label="${occ.video.aria}"></video>
      <figcaption>${occ.creditAmbiances}</figcaption>
    </figure>`;
  const revItems = occ.revelation.map((r, i) => `<li data-zone="${i}"><b>${r.titre}</b> ${r.texte}</li>`).join('');
  const mailto = `mailto:${CONTACT}?subject=${encodeURIComponent(occ.mailtoSubject)}&body=${encodeURIComponent(occ.mailtoBody)}`;
  const spin = occ.spin360 ? `
    <figure class="shots spinwrap">
      <div data-spin360 data-base="${occ.spin360}"></div>
      <figcaption>${occ.creditSpin || ''}</figcaption>
    </figure>` : '';
  root.innerHTML = `
    <p class="kicker">${occ.kicker}</p>
    <h2>${occ.titre} <span class="accent">${occ.titreAccent}</span></h2>
    <p class="lede">${occ.lede}</p>
    ${spin}
    ${photos}
    <div class="price-block">
      <div><span class="k">${occ.prixKicker}</span><strong>${occ.prixLabel.replace('€', '<span class="euro">€</span>')}</strong></div>
      <p>${occ.prixTexte}</p>
    </div>
    <div class="spec-grid">${specRows}</div>
    <div class="revelation">
      <h3>Révélation Honnête</h3>
      <p class="sub">Les défauts d'abord. Les qualités ensuite. Regardez le pont :
        les faisceaux d'inspection s'illuminent quand l'IA en parle.</p>
      <button class="btn" id="btn-revelation" type="button">Lancer la Révélation <span class="arr">&rarr;</span></button>
      <ol class="rev-list" id="rev-list">${revItems}</ol>
    </div>
    <a class="btn btn-solid" href="${mailto}">Je suis intéressé(e) <span class="arr">&rarr;</span></a>
    <p class="footnote">${occ.footnote}</p>`;
  return root;
}

function wirePanel(id, root) {
  if (id === 'occasion') {
    const btn = root.querySelector('#btn-revelation');
    if (btn) btn.addEventListener('click', () => startRevelation(root));
  }
  if (id === 'bureau') wireConfig(root, null);
  if (id === 'galerie') wireGalerie(root);
}

/* --- v3.2 L'ORDRE DE CHASSE : la fiche, la voix, le scellement ---
   Le moment business central du site, traité comme un rituel :
   1. l'avis de recherche s'ÉCRIT sous les yeux (la fiche papier — le seul
      objet clair du monde, comme une fiche sous la lampe du bureau) ;
   2. LA LECTURE DU CHASSEUR répond en direct et sait DIRE NON — calibrée
      sur les fourchettes de chassables.json, celles déjà affichées au mur
      des mandats (données maison, jamais inventées) ;
   3. le scellement au tampon, puis la fiche s'épingle sur la carte de
      France du bureau (pont DOM → monde). Le mail part ensuite, comme avant. */
function wireConfig(root, preselect) {
  const sel = root.querySelector('#hunt-model');
  if (!sel) return;
  S.data.chassables.forEach((c) => {
    const o = document.createElement('option');
    o.value = c.modele; o.textContent = `${c.modele} — ${c.annees}`;
    if (preselect === c.modele) o.selected = true;
    sel.append(o);
  });
  const other = document.createElement('option');
  other.value = '__autre__'; other.textContent = 'Autre — je précise';
  sel.append(other);

  const freeWrap = root.querySelector('#hunt-free-wrap');
  const free = root.querySelector('#hunt-free');
  const range = root.querySelector('#hunt-budget');
  const out = root.querySelector('#hunt-budget-out');
  const kmSel = root.querySelector('#hunt-km');
  const gbSel = root.querySelector('#hunt-gb');
  const seal = root.querySelector('#hunt-seal');
  const read = root.querySelector('#hunter-read');
  const card = {
    root: root.querySelector('#wanted'), date: root.querySelector('#w-date'),
    model: root.querySelector('#w-model'), years: root.querySelector('#w-years'),
    budget: root.querySelector('#w-budget'), km: root.querySelector('#w-km'),
    gb: root.querySelector('#w-gb'), delai: root.querySelector('#w-delai'),
    stamp: root.querySelector('#w-stamp'),
  };
  card.date.textContent = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const chassable = () => S.data.chassables.find((c) => c.modele === sel.value);
  let sealed = false;

  /* la valeur fraîche s'encre sur la fiche */
  const ink = (el, txt) => {
    if (el.textContent === txt) return;
    el.textContent = txt;
    el.classList.remove('ink'); void el.offsetWidth; el.classList.add('ink');
  };
  const syncCard = () => {
    const c = chassable();
    ink(card.model, c ? c.modele : (free.value.trim() || 'À préciser'));
    ink(card.years, c ? `${c.annees} · ${c.motorisation}` : 'hors du mur des chasses — relevé de marché d’abord');
    ink(card.budget, fmt.format(range.value) + ' €');
    ink(card.km, kmSel.value);
    ink(card.gb, gbSel.value);
    ink(card.delai, c ? c.delai : 'après relevé');
  };

  /* LA LECTURE DU CHASSEUR — la bannière (« le seul vendeur qui peut vous
     dire non ») rendue interactive. Fourchettes indicatives relevées,
     les mêmes que la galerie — cohérence maison, zéro chiffre inventé. */
  const readOrder = () => {
    const budget = +range.value;
    const c = chassable();
    let cls, html;
    if (!c) {
      cls = 'hr-ok';
      html = `<b>Commande libre.</b> Ce modèle n’est pas au mur des chasses : la chasse commence par un relevé de marché publié — vous connaîtrez la fourchette réelle <em>avant</em> d’engager quoi que ce soit.`;
    } else {
      const [lo, hi] = c.fourchette;
      if (budget < lo * 0.9) {
        const alt = S.data.chassables
          .filter((x) => x !== c && x.fourchette[0] <= budget)
          .sort((a, b) => b.fourchette[0] - a.fourchette[0])[0];
        cls = 'hr-no';
        html = `<b>Non — pas à ce prix.</b> Une ${c.modele} ${c.annees} saine se chasse entre ${fmt.format(lo)} et ${fmt.format(hi)} € (fourchette indicative relevée, affichée au mur des chasses). À ${fmt.format(budget)} €, je vous trouverais une voiture à problèmes — je préfère vous le dire ici. ${alt ? `Chemin honnête : une ${alt.modele} ${alt.annees} se chasse dès ${fmt.format(alt.fourchette[0])} € — ou remontez le budget.` : `Chemin honnête : remonter le budget, ou élargir les années.`}`;
      } else if (budget < lo) {
        cls = 'hr-warn';
        html = `<b>Jouable, mais serré.</b> La fourchette relevée démarre à ${fmt.format(lo)} € : à ${fmt.format(budget)} €, on trouvera — pas la première main à carnet complet. Pour chasser serein : ${fmt.format(lo)} €.`;
      } else if (budget > hi) {
        cls = 'hr-ok';
        html = `<b>Ordre recevable — un mot d’honnêteté :</b> la fourchette relevée plafonne à ${fmt.format(hi)} €. On chassera sous votre budget : vous ne paierez pas ce que le marché ne demande pas.`;
      } else {
        cls = 'hr-ok';
        html = `<b>Ordre recevable.</b> À ${fmt.format(budget)} €, sur ${c.modele} ${c.annees}, vous êtes dans la fourchette relevée (${fmt.format(lo)}–${fmt.format(hi)} €). La chasse peut s’ouvrir — délai type ${c.delai}.`;
      }
    }
    read.className = cls;
    read.innerHTML = `<span class="hr-tag">La lecture du chasseur</span>${html}`;
  };
  let readTimer = 0;
  const speak = () => { clearTimeout(readTimer); readTimer = setTimeout(readOrder, 420); };

  const unseal = () => {
    if (!sealed) return;
    sealed = false;
    card.stamp.hidden = true;
    card.root.classList.remove('sealed');
    seal.disabled = false;
    root.querySelector('#hunt-recap').hidden = true;
  };
  const onEdit = () => { syncCard(); speak(); unseal(); };
  sel.addEventListener('change', () => { freeWrap.hidden = sel.value !== '__autre__'; onEdit(); });
  free.addEventListener('input', onEdit);
  range.addEventListener('input', () => { out.textContent = fmt.format(range.value) + ' €'; onEdit(); });
  kmSel.addEventListener('change', onEdit);
  gbSel.addEventListener('change', onEdit);
  syncCard(); readOrder();

  root.querySelector('#hunt-form').addEventListener('submit', (e) => {
    e.preventDefault();
    if (sealed) return;
    sealed = true;
    const c = chassable();
    const model = c ? c.modele : (free.value.trim() || 'à préciser');
    const budget = fmt.format(range.value) + ' €';
    const km = kmSel.value, gb = gbSel.value;
    S.lastOrder = { model, years: c ? `${c.annees} · ${c.motorisation}` : '', budget, km, gb, date: card.date.textContent };
    /* le tampon s'abat */
    card.stamp.hidden = false;
    card.root.classList.add('sealed');
    seal.disabled = true;
    sound.blip(130, 0.18, 0.055);
    const fourchette = c ? `\n— Fourchette relevée au mur : ${fmt.format(c.fourchette[0])}–${fmt.format(c.fourchette[1])} €` : '';
    const body = `Bonjour,\n\nMon ordre de chasse (scellé au pavillon le ${card.date.textContent}) :\n— Modèle : ${model}${c ? ` (${c.annees})` : ''}\n— Budget maximum : ${budget}\n— Kilométrage max : ${km}\n— Boîte : ${gb}${fourchette}\n\nMerci de me recontacter pour figer l'ordre de chasse (relevé de marché publié avant tout engagement).\n\nMes coordonnées :`;
    const mailto = `mailto:${CONTACT}?subject=${encodeURIComponent('Ordre de chasse — ' + model)}&body=${encodeURIComponent(body)}`;
    setTimeout(() => {
      const flew = pinCardToBoard();
      const recap = root.querySelector('#hunt-recap');
      recap.hidden = false;
      recap.innerHTML = `<b>${flew ? 'Votre avis de recherche est épinglé à la carte du bureau.' : 'Votre avis de recherche est scellé.'}</b> Reste à nous l'envoyer — on répond par un relevé de marché publié, rien n'est engagé sans lui.<br/><a class="btn btn-solid" href="${mailto}">L'envoyer par email <span class="arr">→</span></a>`;
    }, REDUCED ? 100 : 900);
  });
}

/* v3.2 — la fiche redessinée en canvas pour devenir un objet du monde
   (même famille que texBrume/texDossier : toutes les textures sont maison) */
function letterSpace(x, txt, lx, ly, sp) {
  let cx = lx;
  for (const ch of txt) { x.fillText(ch, cx, ly); cx += x.measureText(ch).width + sp; }
}
function drawWantedTexture(o) {
  const c = document.createElement('canvas'); c.width = 512; c.height = 360;
  const x = c.getContext('2d');
  const gr = x.createLinearGradient(0, 0, 0, 360);
  gr.addColorStop(0, '#f0e8d8'); gr.addColorStop(1, '#e4d8c0');
  x.fillStyle = gr; x.fillRect(0, 0, 512, 360);
  x.globalAlpha = 0.05; x.fillStyle = '#786033';
  for (let i = 0; i < 360; i += 3) x.fillRect(0, i, 512, 1);
  x.globalAlpha = 1;
  x.fillStyle = '#6d5b41'; x.font = '600 15px ui-monospace, monospace';
  letterSpace(x, 'AVIS DE RECHERCHE', 34, 44, 7);
  x.font = '500 13px ui-monospace, monospace';
  x.textAlign = 'right'; x.fillText(o.date, 478, 44); x.textAlign = 'left';
  x.strokeStyle = 'rgba(80,62,38,0.4)'; x.lineWidth = 1;
  x.beginPath(); x.moveTo(34, 58); x.lineTo(478, 58); x.stroke();
  x.fillStyle = '#241d15';
  x.font = `800 ${o.model.length > 18 ? 30 : 38}px 'Archivo V', system-ui, sans-serif`;
  x.fillText(o.model, 34, 112, 444);
  if (o.years) { x.fillStyle = '#6d5b41'; x.font = '500 15px ui-monospace, monospace'; x.fillText(o.years, 34, 140, 444); }
  [['BUDGET MAX', o.budget], ['KILOMETRAGE', o.km], ['BOITE', o.gb]].forEach(([k, v], i) => {
    const gy = 192 + i * 42;
    x.fillStyle = '#8a7457'; x.font = '600 11px ui-monospace, monospace';
    letterSpace(x, k, 34, gy, 2);
    x.fillStyle = '#241d15'; x.font = '700 19px ui-monospace, monospace';
    x.fillText(v, 210, gy);
  });
  x.strokeStyle = 'rgba(80,62,38,0.4)';
  x.beginPath(); x.moveTo(34, 322); x.lineTo(478, 322); x.stroke();
  x.fillStyle = '#8a7457'; x.font = '500 11px ui-monospace, monospace';
  letterSpace(x, 'AUTOMOTIVE EBM · RELEVE DE MARCHE AVANT ENGAGEMENT', 34, 343, 1.5);
  x.save();
  x.translate(392, 240); x.rotate(0.16);
  x.globalAlpha = 0.85; x.strokeStyle = '#b83c00'; x.fillStyle = '#b83c00';
  x.lineWidth = 3; x.strokeRect(-86, -40, 172, 80);
  x.lineWidth = 1.4; x.strokeRect(-79, -33, 158, 66);
  x.font = '800 25px system-ui, sans-serif'; x.textAlign = 'center';
  x.fillText('CHASSE', 0, -8); x.fillText('À OUVRIR', 0, 22);
  x.restore();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/* v3.2 — le vol : la fiche scellée quitte le panneau et s'épingle sur la carte */
const tmpNdc = new THREE.Vector2();
function pinCardToBoard() {
  if (!S.scene || !S.pinBoard || !S.lastOrder) return false;
  if (S.flight) S.flight = null; /* un vol en cours est annulé par le nouveau */
  if (S.pinned) { /* un seul avis épinglé : le vôtre — re-sceller remplace */
    if (S.pinned.parent) S.pinned.parent.remove(S.pinned);
    S.pinned.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); if (o.material.map) o.material.map.dispose(); o.material.dispose(); } });
    S.pinned = null;
  }
  const gCard = new THREE.Group();
  const paper = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.352),
    new THREE.MeshBasicMaterial({ map: drawWantedTexture(S.lastOrder), toneMapped: false })
  );
  gCard.add(paper);
  const pin = new THREE.Mesh(
    new THREE.SphereGeometry(0.016, 12, 10),
    new THREE.MeshStandardMaterial({ color: ACCENT, roughness: 0.35, metalness: 0.2, emissive: ACCENT, emissiveIntensity: 0.25 })
  );
  pin.position.set(0, 0.145, 0.012);
  gCard.add(pin);
  S.pinned = gCard;
  /* sa place sur la carte : bas-droite, un peu de travers, punaisé à la main */
  const local = { pos: new THREE.Vector3(0.52, 1.28, 0.07), rotZ: -0.06 };
  if (REDUCED) { /* pose directe, sans vol */
    S.pinBoard.add(gCard);
    gCard.position.copy(local.pos);
    gCard.rotation.z = local.rotZ;
    return true;
  }
  /* départ : devant la caméra, côté panneau ; arrivée : la place sur la carte */
  ray.setFromCamera(tmpNdc.set(window.innerWidth > 720 ? 0.55 : 0, -0.1), S.camera);
  const from = ray.ray.origin.clone().addScaledVector(ray.ray.direction, 0.85);
  const fromQ = S.camera.quaternion.clone();
  if (!Number.isFinite(from.x)) { /* caméra dégénérée (fenêtre 0) : pose directe, jamais de NaN dans la scène */
    S.pinBoard.add(gCard);
    gCard.position.copy(local.pos);
    gCard.rotation.z = local.rotZ;
    return true;
  }
  S.pinBoard.updateWorldMatrix(true, false);
  const to = local.pos.clone().applyMatrix4(S.pinBoard.matrixWorld);
  const toQ = new THREE.Quaternion();
  S.pinBoard.getWorldQuaternion(toQ);
  toQ.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), local.rotZ));
  gCard.position.copy(from);
  gCard.quaternion.copy(fromQ);
  gCard.scale.setScalar(1.7);
  S.scene.add(gCard);
  S.flight = { mesh: gCard, t0: performance.now(), dur: 1150, from, to, fromQ, toQ, local };
  return true;
}

/* --- la galerie des mandats --- */
function wireGalerie(root) {
  const grid = root.querySelector('#galerie-grid');
  S.data.chassables.forEach((c) => {
    const card = document.createElement('article');
    card.className = 'g-card';
    card.innerHTML = `
      <div class="g-top"><h4>${c.modele}</h4><span class="g-years">${c.annees}</span></div>
      <div class="g-price">${fmt.format(c.fourchette[0])} – ${fmt.format(c.fourchette[1])} €<small>${c.motorisation} · chasse type ${c.delai}</small></div>
      <p>${c.pourquoi}</p>
      <button type="button">Chasser ce modèle →</button>`;
    card.querySelector('button').addEventListener('click', () => {
      const tpl = document.getElementById('tpl-bureau');
      dom.panelC.innerHTML = '';
      dom.panelC.append(tpl.content.cloneNode(true));
      dom.panelC.scrollTop = 0;
      wireConfig(dom.panelC, c.modele);
      sound.click();
    });
    grid.append(card);
  });
}

/* --- Révélation Honnête (faisceaux en séquence) --- */
function startRevelation(root) {
  stopRevelation();
  const lis = [...root.querySelectorAll('#rev-list li')];
  S.rev.idx = -1;
  const step = () => {
    lis.forEach((li) => li.classList.remove('lit'));
    S.rev.targets = [0, 0, 0, 0, 0]; S.rev.body = 0;
    S.rev.idx++;
    if (S.rev.idx > 4) { stopRevelation(); return; }
    const li = lis[S.rev.idx];
    if (li) { li.classList.add('lit'); li.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
    S.rev.targets[S.rev.idx] = 1;
    if (S.rev.idx === 3) S.rev.body = 1;
    sound.blip(520 + S.rev.idx * 60, 0.09, 0.028);
  };
  step();
  S.rev.timer = setInterval(step, 3400);
}
function stopRevelation() {
  if (S.rev.timer) clearInterval(S.rev.timer);
  S.rev.timer = null; S.rev.idx = -1;
  S.rev.targets = [0, 0, 0, 0, 0]; S.rev.body = 0;
  document.querySelectorAll('#rev-list li.lit').forEach((li) => li.classList.remove('lit'));
}

/* ============================================================
   INTERACTIONS (raycast, drag, hover)
   ============================================================ */
const ray = new THREE.Raycaster();
const ptr = { down: false, moved: 0, x: 0, y: 0, ndc: new THREE.Vector2(), active: false };

/* v3.1 le Souffle — l'état du geste : point écran lissé, vitesse, énergie.
   L'énergie monte avec la VITESSE (un passage lent effleure, un geste vif creuse)
   et retombe en ~3 s : la brume se referme derrière le passage.
   ⚠ l'énergie se mesure sur le déplacement BRUT par frame (scalaire — un
   va-et-vient ne s'annule pas) ; la vélocité lissée SIGNÉE ne sert qu'à la
   direction d'entraînement (uFlow) et s'annule légitimement sur un va-et-vient.
   Réglages : gain 0.55/NDC · retombée 0.33/s · lissage position 0.002^dt. */
const breath = { ndc: new THREE.Vector2(), last: new THREE.Vector2(), vel: new THREE.Vector2(), energy: 0, primed: false };

function bindPointer() {
  const el = dom.canvas;
  el.addEventListener('pointerdown', (e) => {
    ptr.down = true; ptr.moved = 0; ptr.x = e.clientX; ptr.y = e.clientY;
  });
  window.addEventListener('pointermove', (e) => {
    ptr.active = true;
    rig.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    rig.mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
    ptr.ndc.set(rig.mouse.x, -rig.mouse.y);
    if (ptr.down && rig.mode === 'orbit') {
      const dx = e.clientX - ptr.x, dy = e.clientY - ptr.y;
      ptr.moved += Math.abs(dx) + Math.abs(dy);
      rig.drag = THREE.MathUtils.clamp(rig.drag + dx * 0.0034, -1.05, 1.05);
      rig.pitch = THREE.MathUtils.clamp(rig.pitch - dy * 0.004, -0.45, 0.85);
      ptr.x = e.clientX; ptr.y = e.clientY;
    }
  });
  window.addEventListener('pointerup', (e) => {
    const wasDrag = ptr.moved > 8;
    ptr.down = false;
    if (wasDrag || S.focused || rig.mode !== 'orbit') return;
    ptr.ndc.set((e.clientX / window.innerWidth) * 2 - 1, -((e.clientY / window.innerHeight) * 2 - 1));
    ray.setFromCamera(ptr.ndc, S.camera);
    const hit = ray.intersectObjects(S.pickables, false)[0];
    if (hit && hit.object.userData.station) openStation(hit.object.userData.station);
  });
}

/* P2 — curseur custom : point net + anneau traînant, 3 états (repos / hover / drag).
   Pointer fin uniquement (CSS le coupe en tactile). Sobre : jamais de texte dedans. */
const cursor = { el: null, ring: null, x: 0, y: 0, rx: 0, ry: 0 };
function bindCursor() {
  cursor.el = $('#cursor');
  if (!cursor.el || window.matchMedia('(pointer: coarse)').matches) { cursor.el = null; return; }
  cursor.ring = cursor.el.querySelector('.ring');
  cursor.dot = cursor.el.querySelector('.dot');
  dom.canvas.addEventListener('pointerenter', (e) => {
    cursor.rx = e.clientX; cursor.ry = e.clientY; // l'anneau se cale, pas de traînée d'entrée
    document.body.classList.add('cur-on');
  });
  dom.canvas.addEventListener('pointerleave', () => document.body.classList.remove('cur-on'));
}
function updateCursor(dt) {
  if (!cursor.el) return;
  const tx = (rig.mouse.x * 0.5 + 0.5) * window.innerWidth;
  const ty = (rig.mouse.y * 0.5 + 0.5) * window.innerHeight;
  cursor.dot.style.transform = `translate(${tx}px, ${ty}px) translate(-50%, -50%)`;
  const k = 1 - Math.pow(0.0001, dt); // l'anneau traîne, le point est net
  cursor.rx += (tx - cursor.rx) * k;
  cursor.ry += (ty - cursor.ry) * k;
  cursor.ring.style.transform = `translate(${cursor.rx}px, ${cursor.ry}px) translate(-50%, -50%)`;
  document.body.classList.toggle('cur-drag', ptr.down && rig.mode === 'orbit');
}

let hoverThrottle = 0;
function updateHover(t) {
  if (t - hoverThrottle < 90) return;
  hoverThrottle = t;
  if (!ptr.active || S.focused || rig.mode !== 'orbit') { setHover(null); return; }
  ray.setFromCamera(ptr.ndc, S.camera);
  const hit = ray.intersectObjects(S.pickables, false)[0];
  setHover(hit ? hit.object.userData.station : null);
}
function setHover(id) {
  if (S.hovered === id) return;
  S.hovered = id;
  document.body.classList.toggle('cur-hover', !!id);
  if (!cursor.el) document.body.style.cursor = id ? 'pointer' : ''; // secours sans curseur custom
  if (id) {
    dom.label.textContent = STATIONS[id].label;
    dom.label.classList.add('on');
    sound.hover();
  } else {
    dom.label.classList.remove('on');
  }
}

/* ============================================================
   LA BOUCLE
   ============================================================ */
const tmpV = new THREE.Vector3();
const huntA = new THREE.Vector3(), huntB = new THREE.Vector3();
function animate() {
  requestAnimationFrame(animate);
  const dt = Math.min(S.clock.getDelta(), 0.05);
  const t = performance.now();
  const et = S.clock.elapsedTime;

  /* caméra */
  if (rig.tween) {
    const k = Math.min(1, (t - rig.tween.t0) / rig.tween.dur);
    const e = easeInOut(k);
    rig.cur.pos.lerpVectors(rig.tween.fp, rig.tween.tp, e);
    rig.cur.tgt.lerpVectors(rig.tween.ft, rig.tween.tt, e);
    if (k >= 1) { const cb = rig.tween.done; rig.tween = null; cb && cb(); }
  } else if (rig.mode === 'orbit') {
    const sway = REDUCED ? 0 : Math.sin(et * 0.07) * 0.045;
    const a = rig.baseAngle + rig.drag + sway;
    const r = 7.0, hgt = 1.85 + rig.pitch - (REDUCED ? 0 : rig.mouse.y * 0.2);
    tmpV.set(Math.sin(a) * r, hgt, -2.0 + Math.cos(a) * r * 1.26);
    rig.cur.pos.lerp(tmpV, 1 - Math.pow(0.001, dt));
    tmpV.set(rig.mouse.x * (REDUCED ? 0 : 0.6), 1.15 - (REDUCED ? 0 : rig.mouse.y * 0.12), -2.0);
    /* P2 — regard magnétique : le regard glisse doucement vers le repère survolé */
    if (S.hovered && !REDUCED && S.markers[S.hovered]) tmpV.lerp(S.markers[S.hovered].position, 0.22);
    rig.cur.tgt.lerp(tmpV, 1 - Math.pow(0.001, dt));
  } else if (rig.mode === 'focus' && !REDUCED) {
    const v = VIEWS[S.focused] || VIEWS.orbit;
    tmpV.copy(v.pos); tmpV.y += Math.sin(et * 0.7) * 0.018;
    rig.cur.pos.lerp(tmpV, 0.02);
  }
  S.camera.position.copy(rig.cur.pos);
  S.camera.lookAt(rig.cur.tgt);

  /* marqueurs : pulsation + bob */
  Object.entries(S.markers).forEach(([id, sp], i) => {
    const k = S.hovered === id ? 1.35 : 1 + (REDUCED ? 0 : Math.sin(et * 2 + i * 1.3) * 0.07);
    sp.scale.set(0.3 * k, 0.3 * k, 1);
    sp.position.y = sp.userData.baseY + (REDUCED ? 0 : Math.sin(et * 1.4 + i) * 0.04);
    sp.material.opacity = S.focused ? 0 : (S.hovered === id ? 1 : 0.8);
  });

  /* label hover : suit le marqueur */
  if (S.hovered && !S.focused) {
    const sp = S.markers[S.hovered];
    tmpV.copy(sp.position).project(S.camera);
    dom.label.style.left = ((tmpV.x * 0.5 + 0.5) * window.innerWidth) + 'px';
    dom.label.style.top = ((-tmpV.y * 0.5 + 0.5) * window.innerHeight) + 'px';
  }

  /* ★ n°1 — les phares : une chasse rentre par la route de la clairière */
  if (S.hunt) {
    const H = S.hunt;
    H.t += dt / H.dur;
    if (H.t > 1 + H.pause / H.dur) H.t = REDUCED ? 0.42 : -0.0001; // repart (reduced : statique posé)
    const k = THREE.MathUtils.clamp(H.t, 0, 1);
    if (REDUCED) { /* posé fixe dans la trouée, sans course */ }
    huntA.lerpVectors(H.p0, H.p1, k);
    huntB.lerpVectors(H.p1, H.p2, k);
    H.g.position.lerpVectors(huntA, huntB, k);
    const fade = Math.min(1, Math.min(k / 0.09, (1 - k) / 0.09));
    const flick = REDUCED ? 1 : THREE.MathUtils.clamp(0.72 + 0.3 * Math.sin(et * 13.7) * Math.sin(et * 7.3), 0.3, 1);
    H.g.children.forEach((sp, i) => {
      sp.material.opacity = (H.t >= 0 && H.t <= 1 ? fade : 0) * flick * (i === 2 ? 0.5 : 0.95);
    });
    /* elles regardent leur route : léger cap vers la direction de course */
    if (k > 0 && k < 1) {
      tmpV.lerpVectors(H.p1, H.p2, k).sub(huntA);
    }
    /* P2 — le son de la chasse : moteur lointain qui suit les phares (opt-in) */
    if (sound.on && !REDUCED) sound.engine(H.t >= 0 && H.t <= 1 ? fade : 0, k);
  }

  /* brume : dérive lente + le Souffle — la matière sent le geste (v3.1) */
  if (!REDUCED) {
    if (ptr.active) {
      if (!breath.primed) { breath.primed = true; breath.ndc.copy(ptr.ndc); breath.last.copy(ptr.ndc); }
      const ddx = ptr.ndc.x - breath.last.x, ddy = ptr.ndc.y - breath.last.y;
      breath.last.copy(ptr.ndc);
      breath.ndc.lerp(ptr.ndc, 1 - Math.pow(0.002, dt));
      const kV = Math.min(1, dt * 5), idt = 1 / Math.max(dt, 1e-3);
      breath.vel.x += (ddx * idt - breath.vel.x) * kV;
      breath.vel.y += (ddy * idt - breath.vel.y) * kV;
      breath.energy = Math.min(1, breath.energy + Math.hypot(ddx, ddy) * 0.55);
    }
    breath.energy = Math.max(0, breath.energy - dt * 0.33);
    const touching = breath.primed && breath.energy > 0.003;
    if (touching) ray.setFromCamera(breath.ndc, S.camera);
    const ro = ray.ray.origin, rd = ray.ray.direction;
    S.mist.forEach((m, i) => {
      m.position.x = m.userData.x0 + Math.sin(et * m.userData.spd + i * 2.1) * m.userData.amp;
      const u = m.material.uniforms;
      u.uOpacity.value = (0.18 + 0.16 * (0.5 + 0.5 * Math.sin(et * 0.05 + i))) * (i === 0 ? 1.4 : 1);
      /* le rayon du regard traverse CE voile (plan z = const) → l'empreinte dans ses UV */
      const tz = touching && Math.abs(rd.z) > 1e-5 ? (m.position.z - ro.z) / rd.z : -1;
      if (tz <= 0) { u.uEnergy.value = 0; return; }
      u.uTouch.value.set(
        (ro.x + rd.x * tz - m.position.x) / m.userData.w + 0.5,
        (ro.y + rd.y * tz - m.position.y) / m.userData.h + 0.5
      );
      u.uEnergy.value = breath.energy;
      u.uFlow.value.set(breath.vel.x, breath.vel.y * 0.55).clampLength(0, 0.32);
    });
  }

  /* poussière dans les rais de l'aube */
  if (S.dust && !REDUCED) {
    const p = S.dust.geometry.attributes.position;
    for (let i = 0; i < p.count; i++) {
      let y = p.getY(i) + dt * 0.04;
      if (y > 4.0) y = 0.1;
      p.setY(i, y);
    }
    p.needsUpdate = true;
    S.dust.rotation.y = et * 0.005;
  }

  /* rais de l'aube : respiration très lente */
  S.shafts.forEach((sh, i) => {
    sh.material.opacity = 0.045 + (REDUCED ? 0 : Math.sin(et * 0.21 + i * 1.7) * 0.02);
  });

  /* écrans : léger scintillement */
  S.screenBlink.forEach((m, i) => {
    m.material.opacity = 0.92 + (REDUCED ? 0 : Math.sin(et * 9 + i * 7) * 0.04);
    m.material.transparent = true;
  });

  /* ★ n°2 — Révélation : orbes + faisceaux vers leurs cibles */
  S.zones.forEach((z, i) => {
    const tgt = S.rev.targets[i] || 0;
    z.light.intensity += (tgt * 2.4 - z.light.intensity) * 0.08;
    z.orb.material.emissiveIntensity += (tgt * 2.0 - z.orb.material.emissiveIntensity) * 0.08;
    const pulse = tgt && !REDUCED ? 1 + Math.sin(et * 5) * 0.25 : 1;
    z.orb.scale.setScalar(pulse);
    z.orb.material.opacity = 0.12 + (z.orb.material.emissiveIntensity / 3) * 0.88;
    const cone = S.cones[i];
    if (cone) cone.material.opacity += (tgt * 0.34 - cone.material.opacity) * 0.09;
  });
  if (S.paintMat) S.paintMat.emissiveIntensity += (S.rev.body * 0.2 - S.paintMat.emissiveIntensity) * 0.06;

  /* ★ n°3 — la Présence : la silhouette passe derrière le dépoli */
  if (S.passer) {
    const P = S.passer;
    if (!P.active && et > P.next) { P.active = true; P.t0 = et; }
    if (P.active) {
      const k = (et - P.t0) / P.dur;
      if (k >= 1) {
        P.active = false;
        P.next = et + 38 + Math.random() * 42;
        P.mesh.material.opacity = 0;
      } else {
        P.mesh.position.z = P.from + (P.to - P.from) * k;
        P.mesh.position.y = 1.02 + Math.sin(k * Math.PI * 9) * 0.02; // le pas
        P.mesh.material.opacity = Math.min(1, Math.min(k / 0.12, (1 - k) / 0.12)) * 0.5;
      }
    }
  }

  /* v3.2 — le vol de l'avis de recherche : du panneau vers la carte du bureau */
  if (S.flight) {
    const F = S.flight;
    const kf = Math.min(1, (t - F.t0) / F.dur);
    const ef = easeInOut(kf);
    F.mesh.position.lerpVectors(F.from, F.to, ef);
    F.mesh.quaternion.slerpQuaternions(F.fromQ, F.toQ, ef);
    F.mesh.scale.setScalar(1.7 - 0.7 * ef);
    if (kf >= 1) {
      S.pinBoard.attach(F.mesh); /* devient enfant de la carte, transform monde conservée */
      F.mesh.position.copy(F.local.pos);
      F.mesh.rotation.set(0, 0, F.local.rotZ);
      F.mesh.scale.setScalar(1);
      sound.blip(680, 0.07, 0.03);
      S.flight = null;
    }
  }

  /* SITE++ — l'aube photo fond sur le shader (courbe douce), puis opaque (perf) */
  if (S.photoSky && S.photoSky.mesh.material.opacity < 1) {
    const kk = Math.min(1, (t - S.photoSky.t0) / 2600);
    S.photoSky.mesh.material.opacity = kk * kk * (3 - 2 * kk);
    if (kk >= 1) S.photoSky.mesh.material.transparent = false;
  }
  /* SITE++ — les pins photo fondent sur les silhouettes, qui s'éteignent */
  if (S.pinsPhoto && S.pinsOld) {
    const kk = Math.min(1, (t - S.pinsPhoto.t0) / (QA ? 1 : 900));
    S.pinsPhoto.groups.forEach((g) => { g.material.opacity = kk; });
    if (kk >= 1) {
      S.pinsPhoto.groups.forEach((g) => { g.material.transparent = false; });
      S.scene.remove(S.pinsOld);
      S.pinsOld.geometry.dispose(); S.pinsOld.material.map.dispose(); S.pinsOld.material.dispose();
      S.pinsOld = null;
    }
  }

  updateHover(t);
  updateCursor(dt);

  /* perf adaptative : mesurée après l'intro */
  if (!S.perf.checked && rig.mode === 'orbit') {
    S.perf.frames++; S.perf.acc += dt;
    if (S.perf.frames >= 140) {
      S.perf.checked = true;
      const fps = S.perf.frames / S.perf.acc;
      if (fps < 42) {
        S.usePost = false;
        S.renderer.setPixelRatio(Math.max(1, Math.min(window.devicePixelRatio, 2) * 0.7));
        console.info('[pavillon] perf mode: post-processing off (' + fps.toFixed(0) + ' fps)');
      }
    }
  }

  if (S.usePost && S.composer) S.composer.render();
  else S.renderer.render(S.scene, S.camera);
}

/* ============================================================
   INIT
   ============================================================ */
function fallback2D(reason) {
  console.warn('[pavillon] fallback 2D :', reason);
  document.body.classList.add('no3d');
  const fb = $('#fallback');
  fb.hidden = false;
  const host = $('#fb-sections');
  ['bureau', 'occasion', 'preuves', 'manifeste', 'prouveur', 'galerie'].forEach((id) => {
    const sec = document.createElement('section');
    if (id === 'occasion' && S.occ) {
      sec.append(buildOccasionPanel(S.occ));
    } else if (id === 'preuves') {
      const tpl = document.getElementById('tpl-preuves');
      if (tpl) sec.append(tpl.content.cloneNode(true));
    } else {
      const tpl = document.getElementById('tpl-' + id);
      if (!tpl) return;
      sec.append(tpl.content.cloneNode(true));
    }
    host.append(sec);
    if (S.data) {
      if (id === 'bureau') wireConfig(sec, null);
      if (id === 'galerie') wireGalerie(sec);
    }
  });
  dom.loader.classList.add('done');
}

async function init() {
  /* données : les chassables + l'occupant du slot */
  try {
    const [ch, oc] = await Promise.all([
      fetch('/assets/data/chassables.json').then((r) => r.json()),
      fetch('/assets/data/occasion.json').then((r) => r.json()),
    ]);
    S.data = ch; S.occ = oc.occupant;
  } catch (e) {
    S.data = { chassables: [] };
    S.occ = null;
  }

  buildRoomsMenu();

  /* WebGL dispo ? */
  let gl = null;
  try { gl = dom.canvas.getContext('webgl2') || dom.canvas.getContext('webgl'); } catch (e) { /* rien */ }
  if (!gl) { fallback2D('WebGL indisponible'); return; }
  if (!S.occ) { fallback2D('données indisponibles'); return; }

  /* police pour les textures canvas */
  try { await document.fonts.load("700 20px 'Archivo V'"); } catch (e) { /* fallback système */ }

  dom.bar.style.width = '30%';

  /* P2 — l'init rend la main entre chaque gros bloc : aucune tâche > 50 ms,
     le TBT s'effondre et le loader reste fluide (critère 3 du bench).
     MessageChannel et pas setTimeout : les timers des onglets en arrière-plan
     sont bridés à 1 s — le postMessage, lui, ne l'est pas. */
  const tickChan = new MessageChannel();
  const tick = () => new Promise((r) => { tickChan.port1.onmessage = () => r(); tickChan.port2.postMessage(0); });

  /* textures */
  const tex = {};
  tex.config = texScreenConfig();
  tex.prouveur = texScreenProuveur();
  await tick();
  tex.carte = texCarteChasse();
  await tick();
  tex.mandats = texMurMandats(S.data.chassables);
  await tick();
  tex.plaqueAtelier = texPlaqueAtelier(S.occ);
  tex.silhouette = texSilhouette();
  await tick();

  /* renderer */
  const renderer = new THREE.WebGLRenderer({ canvas: dom.canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, window.innerWidth < 900 ? 1.5 : 1.75));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.06;
  S.renderer = renderer;

  const camera = new THREE.PerspectiveCamera(46, (window.innerWidth / window.innerHeight) || 1, 0.1, 420); /* v3.2 : || 1 — un boot en fenêtre repliée (0×0) donnait un aspect NaN */
  camera.position.copy(rig.cur.pos);
  S.camera = camera;

  /* scène + brouillard de l'aube */
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(DAWN.fog, 0.0072);
  S.scene = scene;

  dom.bar.style.width = '55%';
  dom.cap.textContent = 'La brume se pose sur la clairière…';

  buildWorld(scene);
  await tick();
  buildPavillon(scene);
  await tick();
  buildBureau(scene, tex);
  buildPreuves(scene);
  await tick();
  buildMandats(scene, tex);
  buildAtelier(scene, tex, S.occ);
  buildProuveur(scene, tex);
  buildPresence(scene, tex);
  await tick();

  /* lumières globales : le soleil rasant + le dôme de l'aube */
  scene.add(new THREE.HemisphereLight(0x51586e, 0x201812, 0.62));
  /* P2 — remplissage mauve très doux depuis le sud-ouest (zéro ombre) :
     la façade d'entrée en bois brûlé gagne du relief au lieu d'être un aplat noir */
  const fill = new THREE.DirectionalLight(0x8a7c9e, 0.5);
  fill.position.set(-14, 9, 22);
  scene.add(fill);
  const sun = new THREE.DirectionalLight(0xff9d50, 2.6);
  sun.position.copy(SUN_DIR).multiplyScalar(70);
  sun.target.position.set(0, 0.4, 2);
  sun.castShadow = true;
  const shadowRes = window.innerWidth < 900 ? 1024 : 2048;
  sun.shadow.mapSize.set(shadowRes, shadowRes);
  sun.shadow.camera.left = -16; sun.shadow.camera.right = 16;
  sun.shadow.camera.top = 14; sun.shadow.camera.bottom = -10;
  sun.shadow.camera.near = 30; sun.shadow.camera.far = 110;
  sun.shadow.bias = -0.0006;
  scene.add(sun, sun.target);

  /* poussière dans la lumière */
  const dustGeo = new THREE.BufferGeometry();
  const N = 360, pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    pos[i * 3] = -10 + Math.random() * 20;
    pos[i * 3 + 1] = Math.random() * 3.8 + 0.1;
    pos[i * 3 + 2] = -6.6 + Math.random() * 13;
  }
  dustGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  S.dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({ color: 0xffd2a8, size: 0.022, transparent: true, opacity: 0.16, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true }));
  scene.add(S.dust);

  /* marqueurs cliquables */
  const mTex = texMarker();
  Object.entries(STATIONS).forEach(([id, st]) => {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: mTex, transparent: true, opacity: 0.85, depthTest: false }));
    sp.scale.set(0.3, 0.3, 1); sp.position.fromArray(st.marker);
    sp.userData.station = id; sp.userData.baseY = st.marker[1];
    scene.add(sp);
    S.markers[id] = sp;
    S.pickables.push(sp);
  });

  dom.bar.style.width = '75%';
  dom.cap.textContent = 'Une chasse rentre par la route…';

  /* environnement PBR : l'aube elle-même éclaire les matières */
  await tick();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  envScene.add(makeSky());
  const envGround = new THREE.Mesh(new THREE.CircleGeometry(200, 24), new THREE.MeshBasicMaterial({ color: 0x171310 }));
  envGround.rotation.x = -Math.PI / 2; envGround.position.y = -1; envScene.add(envGround);
  scene.environment = pmrem.fromScene(envScene, 0.04).texture;
  scene.environmentIntensity = 0.4;
  pmrem.dispose();
  await tick();

  /* post : bloom discret (le soleil, les écrans, les phares) */
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.42, 0.6, 0.74);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());
  /* P2 — la LUT « AUBE EBM » (après OutputPass = espace écran, comme une vraie LUT) :
     ombres poussées vers l'indigo de la nuit qui finit, hautes lumières vers le cuivre,
     courbe S douce, +6 % de saturation. Le même étalonnage que l'usine à visuels. */
  const gradePass = new ShaderPass({
    uniforms: { tDiffuse: { value: null }, uAmt: { value: 1.0 } },
    vertexShader: `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
      varying vec2 vUv;
      uniform sampler2D tDiffuse; uniform float uAmt;
      void main() {
        vec4 c = texture2D(tDiffuse, vUv);
        vec3 col = c.rgb;
        float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
        col += (1.0 - smoothstep(0.0, 0.42, l)) * vec3(-0.010, 0.004, 0.026); /* ombres indigo */
        col += smoothstep(0.58, 1.0, l) * vec3(0.026, 0.010, -0.016);          /* hautes lumières cuivre */
        col = mix(col, col * col * (3.0 - 2.0 * col), 0.16);                    /* courbe S douce */
        col = mix(vec3(dot(col, vec3(0.299, 0.587, 0.114))), col, 1.06);        /* saturation +6 % */
        gl_FragColor = vec4(clamp(mix(c.rgb, col, uAmt), 0.0, 1.0), c.a);
      }`,
  });
  composer.addPass(gradePass);
  S.composer = composer;

  window.addEventListener('resize', () => {
    const w = window.innerWidth, h = window.innerHeight;
    if (!w || !h) return; /* v3.2 : fenêtre réduite à zéro (onglet replié) — une caméra 0/0 devient NaN et casse tout unproject */
    camera.aspect = w / h;
    if (S.focused && w > 720) camera.setViewOffset(w, h, Math.min(275, w * 0.22), 0, w, h);
    else camera.clearViewOffset();
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    composer.setSize(w, h);
  });

  bindPointer();
  bindCursor();

  /* P2 — compilation des shaders EN FOND pendant le loader (pattern Igloo) :
     tous les programmes (scène + bloom + LUT) sont prêts AVANT le premier
     travelling → zéro à-coup à l'entrée. */
  dom.bar.style.width = '85%';
  dom.cap.textContent = 'Le pavillon s’éclaire…';
  try {
    if (renderer.compileAsync && !QA) {
      /* objet par objet + yield : sur les GPU logiciels (SwiftShader), la
         compilation d'un coup est UNE tâche de ~10 s — découpée, elle laisse
         respirer le thread principal (TBT) sans rien changer sur vrai GPU */
      for (const child of scene.children) {
        await renderer.compileAsync(child, camera, scene);
        await tick();
      }
    }
  } catch (e) { /* compile au premier rendu, simplement moins lisse */ }
  composer.render(); // chauffe les passes (bloom + LUT) hors écran du visiteur

  animate();

  /* hook QA (console) — n'expose rien de sensible */
  window.EBM = { open: openStation, close: closePanel, S };

  dom.bar.style.width = '100%';
  dom.cap.textContent = 'L’aube est là.';

  /* entrée en scène : on arrive par la clairière, on entre, on fait face au bureau */
  const enter = () => {
    dom.loader.classList.add('done');
    dom.loader.setAttribute('aria-hidden', 'true');
    dom.entry.classList.add('show');
    tweenTo(VIEWS.orbit, REDUCED ? 0.03 : 4.6, () => { rig.mode = 'orbit'; });
    setTimeout(() => dom.hint.classList.add('show'), REDUCED ? 400 : 3400);
    setTimeout(() => { if (!S.focused) dom.entry.classList.add('hide'); }, 9500);
    setTimeout(loadPhotoSky, QA ? 0 : (REDUCED ? 500 : 2400)); // SITE++ : l'aube réelle s'allume pendant le travelling
    setTimeout(loadPhotoPins, QA ? 0 : (REDUCED ? 700 : 3200)); // SITE++ : la forêt devient vraie juste après
    setTimeout(loadPBRFloors, QA ? 0 : (REDUCED ? 900 : 4000)); // SITE++ : le relief du béton sous le rasant
    setTimeout(loadChevalet, QA ? 0 : (REDUCED ? 1100 : 4700)); // SITE++ : la photo réelle entre dans le monde
    if (QA) {
      const vue = (window.location.hash.match(/#vue=(\w+)/) || [])[1];
      rig.tween = null; rig.mode = 'orbit';
      rig.cur.pos.copy(VIEWS.orbit.pos); rig.cur.tgt.copy(VIEWS.orbit.tgt);
      if (vue && VIEWS[vue]) { rig.cur.pos.copy(VIEWS[vue].pos); rig.cur.tgt.copy(VIEWS[vue].tgt); rig.mode = 'focus'; S.focused = vue; }
    }
  };
  setTimeout(enter, QA ? 60 : 350);
}

init().catch((e) => { console.error(e); fallback2D(e.message || 'erreur init'); });
// MODIF COLONNE BUSINESS FABLE 5.1 V2 (Fable 5.1, carnet 381075be) | 2026-09-06 17:15 | feuille RELEVE MARCHE : « 11 annonces comparables » (releve du 24/08, perime) remplace par la date du releve du 30/08/2026 ; rollback : pavillon.js.bak_20260906_171557_position_datee
